import { useState, useEffect, useRef, useCallback } from "react";

// ── HOOK MOBILE ──────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── DATOS INICIALES ──────────────────────────────────────────────
const VENDEDORES = [
  { id: "admin", nombre: "Admin", pin: "1234", rol: "admin" },
];

const REGIONES_INICIALES = ["LATAM", "China", "France", "Italy", "Germany"];
const EMPRESAS_CAPTIO = ["All", "FL"];

// Paleta FL — navy + gold
const C = {
  navy:      "#1A2B4A",
  navyMid:   "#2D4470",
  navyLite:  "#6B7A94",
  gold:      "#C8972A",
  bg:        "#F5F4F0",
  bgWarm:    "#ECEAE4",
  rule:      "rgba(26,43,74,0.12)",
  accent:    "#C8972A",
};

const COLUMNAS = [
  { id: "Nuevo",     label: "New",       color: "#6B7A94", bg: "rgba(107,122,148,0.10)" },
  { id: "Validado",  label: "Validated", color: "#2D4470", bg: "rgba(45,68,112,0.10)"    },
  { id: "Cotizado",  label: "Quoted",    color: "#C8972A", bg: "rgba(107,114,128,0.10)" },
  { id: "Ganado",    label: "Won",       color: "#1A2B4A", bg: "rgba(26,43,74,0.10)"    },
  { id: "Perdido",   label: "Lost",      color: "#EF4444", bg: "rgba(239,68,68,0.10)"   },
];

const FUENTE_ICON = {
  "SerpAPI": "🔍", "LinkedIn": "💼", "Manual": "📁",
  "Referral": "🤝", "Web": "🌐", "Email": "📧",
};

const LEADS_DEMO = [
  { id:1, empresa:"TechStar Shanghai Ltd", contacto:"Li Wei", cargo:"IP Manager", email:"lwei@techstar.cn", tel:"+86 21 5555 0001", ciudad:"Shanghai", region:"China", segmento:"Technology", fuente:"SerpAPI", estado:"Nuevo", vendedor:"admin", score:91, monto:0, fecha:"2026-06-01", notas:"Expanding to LATAM market", empresa_captio:"FL" },
  { id:2, empresa:"Maison Beauté Paris SAS", contacto:"Claire Dupont", cargo:"Legal Director", email:"cdupont@maisonbeaute.fr", tel:"+33 1 4455 6677", ciudad:"Paris", region:"France", segmento:"Fashion & Luxury", fuente:"LinkedIn", estado:"Validado", vendedor:"admin", score:87, monto:0, fecha:"2026-06-03", notas:"Needs trademark registration in Argentina and Brazil", empresa_captio:"FL" },
  { id:3, empresa:"Grupo Innovación SA", contacto:"Carlos Méndez", cargo:"CEO", email:"cmendez@grupoinnovacion.com", tel:"+54 11 4567 8901", ciudad:"Buenos Aires", region:"LATAM", segmento:"Technology", fuente:"SerpAPI", estado:"Cotizado", vendedor:"admin", score:94, monto:2800, fecha:"2026-06-05", notas:"Quote sent for LATAM trademark package", empresa_captio:"FL" },
  { id:4, empresa:"Automazione Italia SpA", contacto:"Marco Rossi", cargo:"Head of Legal", email:"mrossi@automazione.it", tel:"+39 02 1234 5678", ciudad:"Milano", region:"Italy", segmento:"Industrial", fuente:"LinkedIn", estado:"Ganado", vendedor:"admin", score:89, monto:3500, fecha:"2026-05-28", notas:"Argentina + Brazil trademark registration confirmed", empresa_captio:"FL" },
  { id:5, empresa:"Chemie AG Hamburg", contacto:"Hans Müller", cargo:"IP Counsel", email:"hmuller@chemieag.de", tel:"+49 40 9876 5432", ciudad:"Hamburg", region:"Germany", segmento:"Chemical & Pharma", fuente:"Manual", estado:"Nuevo", vendedor:"admin", score:76, monto:0, fecha:"2026-06-08", notas:"Interested in patent filing in Argentina", empresa_captio:"FL" },
];

// ── HELPERS ──────────────────────────────────────────────────────
const fmt$ = v => v ? `USD ${Number(v).toLocaleString("en-US")}` : "—";
const scoreColor = s => s >= 85 ? "#10B981" : s >= 68 ? "#F59E0B" : "#EF4444";

function useLocalStorage(key, initial) {
  const [val, setVal] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; }
    catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }, [key, val]);
  return [val, setVal];
}

// Hook para cargar leads reales — siempre pisa los datos demo
function useLeadsFromFile(setLeads) {
  useEffect(() => {
    fetch("https://fl.velqo.io/api/leads")
      .then(r => {
        if (!r.ok) throw new Error("Sin servidor");
        return r.json();
      })
      .then(data => {
        if (!Array.isArray(data) || data.length === 0) return;
        const normalized = data.map((l, i) => ({
          id:       l.id || i + 1,
          empresa:  l.empresa || "",
          contacto: l.contacto || l.nombre_contacto || "",
          cargo:    l.cargo_decisor || l.cargo || "",
          email:    l.email_estimado || l.email || "",
          tel:      l.telefono || l.tel || "",
          ciudad:   l.ciudad || "",
          region:   l.region || "CABA",
          segmento: l.segmento || "",
          fuente:   l.fuente || "Agent AI",
          estado:   l.estado || "Nuevo",
          vendedor: l.vendedor || "",
          score:    Number(l.score) || 70,
          monto:    Number(l.monto) || 0,
          fecha:    l.fecha || new Date().toISOString().split("T")[0],
          notas:          l.notas || "",
          empresa_captio: l.empresa_captio || "FL",
        }));
        // Limpiar localStorage y setear datos reales
        localStorage.removeItem("fl_leads");
        setLeads(normalized);
      })
      .catch(err => {
        console.warn("Server no disponible, usando datos locales:", err);
      });
  }, []);
}

// ── COMPONENTES BASE ─────────────────────────────────────────────
function ScoreBar({ score }) {
  const c = scoreColor(score);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ flex:1, height:3, background:"rgba(255,255,255,0.08)", borderRadius:2, overflow:"hidden" }}>
        <div style={{ width:`${score}%`, height:"100%", background:c, borderRadius:2 }}/>
      </div>
      <span style={{ fontSize:10, color:c, fontWeight:700, minWidth:22 }}>{score}</span>
    </div>
  );
}

// ── MODAL MOVER LEAD ─────────────────────────────────────────────
function ModalMover({ lead, destino, onConfirm, onCancel }) {
  const [monto, setMonto] = useState(lead.monto || "");
  const [motivo, setMotivo] = useState("");

  const necesitaMonto  = destino === "Cotizado" || destino === "Ganado";
  const necesitaMotivo = destino === "Perdido";

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16,
    }}>
      <div style={{
        background:"#F5F4F0", border:"1px solid rgba(26,43,74,0.12)",
        borderRadius:16, padding:28, width:"100%", maxWidth:400,
      }}>
        <div style={{ fontSize:16, fontWeight:700, color:"#1A2B4A", marginBottom:6 }}>
          Mover a <span style={{ color: COLUMNAS.find(c=>c.id===destino)?.color }}>{destino}</span>
        </div>
        <div style={{ fontSize:13, color:"rgba(26,43,74,0.5)", marginBottom:20 }}>
          {lead.empresa}
        </div>

        {necesitaMonto && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:1 }}>
              {destino === "Cotizado" ? "Monto del presupuesto (ARS)" : "Monto del cierre (ARS)"}
            </label>
            <input
              type="number"
              value={monto}
              onChange={e => setMonto(e.target.value)}
              placeholder="ej: 150000"
              style={{
                width:"100%", marginTop:6, background:"rgba(255,255,255,0.06)",
                border:"1px solid rgba(255,255,255,0.12)", borderRadius:8,
                padding:"10px 14px", color:"#1A2B4A", fontSize:14, outline:"none", boxSizing:"border-box",
              }}
            />
          </div>
        )}

        {necesitaMotivo && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:1 }}>
              Motivo de pérdida
            </label>
            <select
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              style={{
                width:"100%", marginTop:6, background:"#ffffff",
                border:"1px solid rgba(26,43,74,0.15)", borderRadius:8,
                padding:"10px 14px", color:"#1A2B4A", fontSize:13, outline:"none", boxSizing:"border-box",
              }}
            >
              <option value="">Seleccionar motivo...</option>
              <option value="Precio">Precio — eligió otro proveedor más barato</option>
              <option value="Sin respuesta">Sin respuesta — no contestó</option>
              <option value="No calificó">No calificó — no era el perfil</option>
              <option value="Competencia">Competencia — eligió competidor</option>
              <option value="Postergado">Postergado — no es el momento</option>
              <option value="Otro">Otro motivo</option>
            </select>
          </div>
        )}

        <div style={{ display:"flex", gap:10, marginTop:8 }}>
          <button onClick={onCancel} style={{
            flex:1, padding:"10px 0", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
            background:"transparent", color:"rgba(26,43,74,0.5)", cursor:"pointer", fontSize:13,
          }}>Cancelar</button>
          <button
            onClick={() => onConfirm({ monto: Number(monto) || 0, motivo })}
            disabled={necesitaMotivo && !motivo}
            style={{
              flex:2, padding:"10px 0", borderRadius:8, border:"none",
              background: COLUMNAS.find(c=>c.id===destino)?.color || "#1A2B4A",
              color:"#F5F4F0", cursor:"pointer", fontSize:13, fontWeight:700,
              opacity: (necesitaMotivo && !motivo) ? 0.4 : 1,
            }}
          >Confirmar</button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL DETALLE LEAD ───────────────────────────────────────────
function ModalDetalle({ lead, vendedores, onClose, onAsignar, esAdmin, usuarioActual }) {
  const [mostrarPresupuesto, setMostrarPresupuesto] = useState(false);
  const [descPresupuesto, setDescPresupuesto]       = useState("");
  const [montoNeto, setMontoNeto]                   = useState("");
  const [generando, setGenerando]                   = useState(false);
  const [pdfResult, setPdfResult]                   = useState(null);
  const [enviandoEmail, setEnviandoEmail]           = useState(null); // "presentacion" | "seguimiento" | null
  const [emailLog, setEmailLog]                     = useState([]);
  const [loadingLog, setLoadingLog]                 = useState(false);

  const empKey = "fl";

  const vendedor = vendedores.find(v => v.id === lead.vendedor);

  // Cargar historial de emails de este lead al abrir el modal
  useEffect(() => {
    setLoadingLog(true);
    fetch(`https://fl.velqo.io/api/actividad?lead_id=${lead.id}&limit=20`)
      .then(r => r.json())
      .then(data => { setEmailLog(Array.isArray(data) ? data : []); setLoadingLog(false); })
      .catch(() => setLoadingLog(false));
  }, [lead.id]);

  const enviarEmail = (tipo) => {
    const labels = { presentacion: "introduction email", "seguimiento": "follow-up email" };
    if (!window.confirm(`¿Confirmar envío de ${labels[tipo]} a ${lead.empresa}?`)) return;
    setEnviandoEmail(tipo);
    fetch(`https://fl.velqo.io/api/email/${tipo}/${empKey}/${lead.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enviado_por: usuarioActual?.id || "sistema" }),
    })
    .then(r => r.json())
    .then(d => {
      setEnviandoEmail(null);
      if (d.ok) {
        alert(`✅ Email enviado correctamente`);
        // Refrescar log
        fetch(`https://fl.velqo.io/api/actividad?lead_id=${lead.id}&limit=20`)
          .then(r => r.json())
          .then(data => setEmailLog(Array.isArray(data) ? data : []))
          .catch(() => {});
      } else {
        alert(`❌ Error al enviar: ${d.error}`);
      }
    })
    .catch(() => { setEnviandoEmail(null); alert("❌ Error de conexión"); });
  };

  const generarPDF = () => {
    if (!descPresupuesto || !montoNeto) return alert("Completá la descripción y el monto");
    setGenerando(true);
    fetch("https://fl.velqo.io/api/presupuesto", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        empresa_key:     empKey,
        lead_id:         lead.id,
        descripcion:     descPresupuesto,
        monto_neto:      montoNum,
        vendedor_nombre: vendedor ? vendedor.nombre : "Equipo Comercial",
        vendedor_email:  "hola@fernandezlacort.com.ar",
      }),
    })
    .then(r => r.json())
    .then(d => {
      setGenerando(false);
      if (d.ok) setPdfResult(d);
      else alert("Error: " + d.error);
    })
    .catch(() => { setGenerando(false); alert("Error al generar"); });
  };


  const pdfUrl     = pdfResult ? `https://fl.velqo.io${pdfResult.url}` : "";
  const waMsg      = pdfResult ? encodeURIComponent(`Hola ${lead.contacto || lead.empresa}, le adjuntamos el presupuesto N° ${pdfResult.numero} por €${pdfResult.total?.toLocaleString("es-ES")}. Puede descargarlo aquí: ${pdfUrl}`) : "";
  const montoNum   = (() => { const n = parseFloat(String(montoNeto).replace(/[^0-9.]/g,"")); return isNaN(n) ? 0 : n; })();
  const iva        = montoNum > 0 ? Math.round(montoNum * 0.21 * 100) / 100 : 0;
  const total      = montoNum > 0 ? Math.round((montoNum + iva) * 100) / 100 : 0;
  const formatWATel = (tel) => {
    if (!tel) return "";
    let n = tel.replace(/[^0-9]/g, "");
    if (n.startsWith("0")) n = n.slice(1);
    if (n.startsWith("54")) return n;
    return "549" + n;
  };
  const waTel  = formatWATel(lead.tel);
  const waLink = waTel ? `https://wa.me/${waTel}?text=${waMsg}` : `https://wa.me/?text=${waMsg}`;

  return (
    <div className="modal-overlay" style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16,
    }}>
      <div className="modal-inner" style={{
        background:"#F5F4F0", border:"1px solid rgba(26,43,74,0.1)",
        borderRadius:16, padding:28, width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:800, color:"#1A2B4A" }}>{lead.empresa}</div>
            <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)", marginTop:2 }}>{lead.segmento} · {lead.region}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"rgba(26,43,74,0.4)", cursor:"pointer", fontSize:20 }}>✕</button>
        </div>

        {[
          ["Contacto", lead.contacto],
          ["Cargo", lead.cargo],
          ["Email", lead.email],
          ["Teléfono", lead.tel],
          ["Ciudad", lead.ciudad],
          ["Fuente", lead.fuente],
          ["Fecha", lead.fecha],
          ["Notas", lead.notas],
          lead.monto ? ["Monto", fmt$(lead.monto)] : null,
        ].filter(Boolean).map(([k,v]) => v ? (
          <div key={k} style={{ display:"flex", gap:12, marginBottom:10 }}>
            <span style={{ fontSize:11, color:"rgba(26,43,74,0.35)", minWidth:70, textTransform:"uppercase", letterSpacing:.5, paddingTop:1 }}>{k}</span>
            <span style={{ fontSize:13, color:"rgba(26,43,74,0.8)", flex:1 }}>{v}</span>
          </div>
        ) : null)}

        <div style={{ marginTop:8, marginBottom:16 }}>
          <span style={{ fontSize:11, color:"rgba(26,43,74,0.35)", textTransform:"uppercase", letterSpacing:.5 }}>Score</span>
          <div style={{ marginTop:6 }}><ScoreBar score={lead.score}/></div>
        </div>

        {/* ── EMAILS ── */}
        <div style={{ marginTop:20, borderTop:"1px solid rgba(26,43,74,0.08)", paddingTop:16 }}>
          <div style={{ fontSize:11, color:"rgba(26,43,74,0.35)", textTransform:"uppercase", letterSpacing:.5, marginBottom:10 }}>
            Envío de emails
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:14 }}>
            <button
              onClick={() => enviarEmail("presentacion")}
              disabled={!!enviandoEmail}
              style={{
                flex:1, padding:"9px 0", borderRadius:8, border:"none",
                background: enviandoEmail === "presentacion" ? "rgba(26,43,74,0.05)" : "rgba(26,43,74,0.08)",
                color: enviandoEmail === "presentacion" ? "#6B7A94" : "#1A2B4A",
                fontWeight:700, fontSize:12, cursor: enviandoEmail ? "not-allowed" : "pointer",
                fontFamily:"Georgia,serif", fontStyle:"italic",
              }}
            >
              {enviandoEmail === "presentacion" ? "⏳ Enviando..." : "✉ Introduction email"}
            </button>
          </div>

          {/* Historial de emails de este lead */}
          {loadingLog && <div style={{ fontSize:11, color:"rgba(26,43,74,0.3)" }}>Cargando historial...</div>}
          {!loadingLog && emailLog.length > 0 && (
            <div>
              <div style={{ fontSize:10, color:"rgba(26,43,74,0.25)", marginBottom:6 }}>Historial de contacto</div>
              {emailLog.map(log => {
                const tipoLabel = { presentacion:"Introduction", "seguimiento":"Follow-up", presupuesto:"Presupuesto" }[log.tipo] || log.tipo;
                const fecha = new Date(log.creado_en).toLocaleString("es-AR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" });
                return (
                  <div key={log.id} style={{
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"5px 8px", borderRadius:6, marginBottom:4,
                    background: log.estado === "enviado" ? "rgba(16,185,129,0.07)" : "rgba(239,68,68,0.07)",
                    border:`1px solid ${log.estado === "enviado" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)"}`,
                  }}>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <span style={{ fontSize:10, color: log.estado === "enviado" ? "#10B981" : "#EF4444" }}>
                        {log.estado === "enviado" ? "✓" : "✗"}
                      </span>
                      <span style={{ fontSize:11, color:"rgba(26,43,74,0.65)" }}>{tipoLabel}</span>
                      {log.enviado_por && log.enviado_por !== "sistema" && (
                        <span style={{ fontSize:9, color:"rgba(26,43,74,0.3)" }}>· {log.enviado_por}</span>
                      )}
                    </div>
                    <span style={{ fontSize:10, color:"rgba(26,43,74,0.3)" }}>{fecha}</span>
                  </div>
                );
              })}
            </div>
          )}
          {!loadingLog && emailLog.length === 0 && (
            <div style={{ fontSize:11, color:"rgba(26,43,74,0.2)", textAlign:"center", padding:"6px 0" }}>
              Sin emails enviados aún
            </div>
          )}
        </div>

        {/* ── PRESUPUESTO ── */}
        <div style={{ marginTop:20, borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:16 }}>
          <button onClick={() => { setMostrarPresupuesto(!mostrarPresupuesto); setPdfResult(null); }} style={{
            width:"100%", padding:"10px", borderRadius:8, border:"none",
            background: mostrarPresupuesto ? "rgba(255,107,0,0.2)" : "rgba(255,107,0,0.1)",
            color:"#1A2B4A", fontWeight:700, fontSize:13, cursor:"pointer",
          }}>
            📄 {mostrarPresupuesto ? "Cerrar presupuesto" : "Generar presupuesto"}
          </button>

          {mostrarPresupuesto && !pdfResult && (
            <div style={{ marginTop:14 }}>
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, color:"rgba(26,43,74,0.35)", textTransform:"uppercase", letterSpacing:.5, display:"block", marginBottom:6 }}>
                  Descripción del servicio
                </label>
                <textarea
                  value={descPresupuesto}
                  onChange={e => setDescPresupuesto(e.target.value)}
                  placeholder="Ej: En AArti ofrecemos distintos paquetes de horas para que puedan vivir la experiencia."
                  rows={4}
                  style={{
                    width:"100%", background:"#ffffff", border:"1px solid rgba(26,43,74,0.15)",
                    borderRadius:8, padding:"10px 12px", color:"#1A2B4A", fontSize:12,
                    outline:"none", resize:"vertical", boxSizing:"border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, color:"rgba(26,43,74,0.35)", textTransform:"uppercase", letterSpacing:.5, display:"block", marginBottom:6 }}>
                  Monto neto (sin IVA)
                </label>
                <input
                  type="text"
                  value={montoNeto}
                  onChange={e => setMontoNeto(e.target.value)}
                  placeholder="Ej: 45000"
                  style={{
                    width:"100%", background:"#ffffff", border:"1px solid rgba(26,43,74,0.15)",
                    borderRadius:8, padding:"10px 12px", color:"#1A2B4A", fontSize:13,
                    outline:"none", boxSizing:"border-box",
                  }}
                />
              </div>

              {montoNum > 0 && (
                <div style={{ background:"rgba(255,107,0,0.08)", border:"1px solid rgba(255,107,0,0.2)", borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"rgba(26,43,74,0.5)", marginBottom:4 }}>
                    <span>Subtotal</span><span>€{montoNum.toLocaleString("es-ES")}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"rgba(26,43,74,0.5)", marginBottom:6 }}>
                    <span>IVA 21%</span><span>€{iva.toLocaleString("es-ES")}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:14, fontWeight:800, color:"#1A2B4A", borderTop:"1px solid rgba(255,107,0,0.2)", paddingTop:6 }}>
                    <span>TOTAL</span><span>€{total.toLocaleString("es-ES")}</span>
                  </div>
                </div>
              )}

              <button onClick={generarPDF} disabled={generando} style={{
                width:"100%", padding:"12px", borderRadius:8, border:"none",
                background: generando ? "#374151" : "#1A2B4A",
                color: generando ? "#9CA3AF" : "#F5F4F0",
                fontWeight:700, fontSize:13, cursor: generando ? "not-allowed" : "pointer",
              }}>
                {generando ? "⏳ Generando PDF..." : "📄 Generar PDF"}
              </button>
            </div>
          )}

          {pdfResult && (
            <div style={{ marginTop:14 }}>
              <div style={{ background:"rgba(16,185,129,0.1)", border:"1px solid rgba(16,185,129,0.3)", borderRadius:8, padding:"12px 14px", marginBottom:12, textAlign:"center" }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#10B981", marginBottom:2 }}>✅ Presupuesto generado</div>
                <div style={{ fontSize:11, color:"rgba(26,43,74,0.4)" }}>N° {pdfResult.numero} · Total €{pdfResult.total?.toLocaleString("es-ES")}</div>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <a href={pdfUrl} target="_blank" rel="noreferrer" style={{
                  display:"block", textAlign:"center", padding:"10px", borderRadius:8,
                  background:"rgba(255,255,255,0.08)", color:"#1A2B4A", textDecoration:"none",
                  fontSize:13, fontWeight:600,
                }}>📥 Descargar PDF</a>

                <a href={`mailto:${lead.email}?subject=Proposal N° ${pdfResult.numero} — Fernández Lacort&body=Estimado ${lead.contacto || lead.empresa},%0D%0A%0D%0AAdjuntamos el presupuesto solicitado.%0D%0ADescargue el PDF aquí: ${pdfUrl}%0D%0A%0D%0ASaludos,%0D%0AAdmin — Fernández Lacort`} style={{
                  display:"block", textAlign:"center", padding:"10px", borderRadius:8,
                  background:"rgba(59,130,246,0.15)", color:"#3B82F6", textDecoration:"none",
                  fontSize:13, fontWeight:600,
                }}>📧 Enviar por email</a>

                <a href={waLink} target="_blank" rel="noreferrer" style={{
                  display:"block", textAlign:"center", padding:"10px", borderRadius:8,
                  background:"rgba(37,211,102,0.15)", color:"#25D366", textDecoration:"none",
                  fontSize:13, fontWeight:600,
                }}>💬 Enviar por WhatsApp</a>

                <button onClick={() => { setPdfResult(null); setDescPresupuesto(""); setMontoNeto(""); }} style={{
                  padding:"8px", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
                  background:"transparent", color:"rgba(26,43,74,0.4)", cursor:"pointer", fontSize:12,
                }}>Nuevo presupuesto</button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── CARD LEAD ────────────────────────────────────────────────────
function LeadCard({ lead, columnaColor, onDragStart, onTap, esAdmin, vendedores }) {
  const vendedor = vendedores.find(v => v.id === lead.vendedor);
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, lead.id)}
      onClick={() => onTap(lead)}
      style={{
        background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.07)",
        borderLeft:`3px solid ${columnaColor}`, borderRadius:10, padding:"12px 14px",
        cursor:"grab", marginBottom:8, transition:"transform .15s, box-shadow .15s",
        userSelect:"none",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.3)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}
    >
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
        {(() => {
          const empColors = {
            "FL":         { bg:"rgba(200,151,42,0.15)", color:"#1A2B4A" },
          };
          const ec = empColors[lead.empresa_captio] || { bg:"rgba(139,92,246,0.15)", color:"#8B5CF6" };
          return (
            <span style={{
              fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10,
              background:ec.bg, color:ec.color, whiteSpace:"nowrap",
            }}>{lead.empresa_captio || "FL"}</span>
          );
        })()}
      </div>
      <div style={{ fontSize:13, fontWeight:700, color:"#1A2B4A", marginBottom:2, lineHeight:1.3 }}>{lead.empresa}</div>
      <div style={{ fontSize:11, color:"rgba(26,43,74,0.45)", marginBottom:8 }}>{lead.contacto} · {lead.cargo}</div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
        <span style={{ fontSize:10, color:"rgba(26,43,74,0.3)" }}>{FUENTE_ICON[lead.fuente] || "📌"} {lead.fuente}</span>
        <span style={{ fontSize:10, color:"rgba(26,43,74,0.3)" }}>{lead.region}</span>
      </div>
      <ScoreBar score={lead.score}/>
      {lead.monto > 0 && (
        <div style={{ marginTop:6, fontSize:12, fontWeight:700, color:"#F59E0B" }}>{fmt$(lead.monto)}</div>
      )}
      {esAdmin && vendedor && (
        <div style={{ marginTop:6, fontSize:10, color:"rgba(26,43,74,0.3)" }}>👤 {vendedor.nombre}</div>
      )}
    </div>
  );
}

// ── COLUMNA KANBAN ───────────────────────────────────────────────
function Columna({ col, leads, onDrop, onDragOver, onDragStart, onTap, esAdmin, vendedores }) {
  const total$ = leads.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0);
  return (
    <div
      style={{ flex:1, minWidth:0 }}
      onDragOver={onDragOver}
      onDrop={e => onDrop(e, col.id)}
    >
      <div style={{
        background: col.bg, border:`1px solid ${col.color}33`,
        borderRadius:12, padding:"10px 12px", marginBottom:8,
        display:"flex", justifyContent:"space-between", alignItems:"center",
      }}>
        <div>
          <span style={{ fontSize:12, fontWeight:700, color:col.color }}>{col.label}</span>
          <span style={{ fontSize:11, color:"rgba(26,43,74,0.35)", marginLeft:8 }}>{leads.length}</span>
        </div>
        {total$ > 0 && <span style={{ fontSize:11, fontWeight:700, color:col.color }}>{fmt$(total$)}</span>}
      </div>
      <div style={{ minHeight:120 }}>
        {leads.map(lead => (
          <LeadCard
            key={lead.id}
            lead={lead}
            columnaColor={col.color}
            onDragStart={onDragStart}
            onTap={onTap}
            esAdmin={esAdmin}
            vendedores={vendedores}
          />
        ))}
      </div>
    </div>
  );
}

// ── LOGIN ────────────────────────────────────────────────────────
function Login({ vendedores, onLogin }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const intentar = () => {
    const v = vendedores.find(v => v.pin === pin);
    if (v) { setError(false); onLogin(v); }
    else { setError(true); setTimeout(() => setError(false), 2000); }
  };

  return (
    <div style={{
      minHeight:"100vh", background:"#1A2B4A", display:"flex",
      alignItems:"center", justifyContent:"center", padding:24,
      fontFamily:"Georgia,'Times New Roman',serif",
    }}>
      <div style={{ textAlign:"center", maxWidth:360, width:"100%" }}>
        <div style={{
          fontFamily:"Georgia,serif", fontSize:22, fontWeight:"normal",
          color:"#C8972A", letterSpacing:"0.12em", marginBottom:8,
        }}>FERNÁNDEZ LACORT</div>
        <div style={{
          fontSize:11, letterSpacing:"0.22em", textTransform:"uppercase",
          color:"rgba(255,255,255,0.4)", marginBottom:48,
        }}>Intellectual Property · Latin America</div>

        <input
          type="password"
          value={pin}
          onChange={e => setPin(e.target.value)}
          onKeyDown={e => e.key === "Enter" && intentar()}
          placeholder="PIN"
          maxLength={6}
          style={{
            width:"100%", padding:"14px", textAlign:"center", fontSize:20,
            letterSpacing:"0.3em", background:"rgba(255,255,255,0.06)",
            border:`1px solid ${error ? "#EF4444" : "rgba(255,255,255,0.15)"}`,
            borderRadius:8, color:"#fff", outline:"none",
            boxSizing:"border-box", marginBottom:16, fontFamily:"Georgia,serif",
          }}
        />
        <button
          onClick={intentar}
          style={{
            width:"100%", padding:"14px", background:"#C8972A", color:"#1A2B4A",
            border:"none", fontFamily:"Georgia,serif", fontSize:13,
            fontWeight:700, cursor:"pointer", borderRadius:8, letterSpacing:"0.05em",
            transition:"opacity 0.2s",
          }}
          onMouseEnter={e => e.target.style.opacity = "0.85"}
          onMouseLeave={e => e.target.style.opacity = "1"}
        >
          {error ? "Incorrect PIN" : "Enter"}
        </button>

        <div style={{ marginTop:48, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"rgba(255,255,255,0.2)" }}>
          by velqo.io
        </div>
      </div>
    </div>
  );
}


// ── PANEL ADMIN — Agente ─────────────────────────────────────────
function PanelAgente({ regiones, onAgregarRegion }) {
  const [cfg, setCfg] = useState({ segmento:"construcción", ciudad:"Córdoba", radio:"40" });
  const [corriendo, setCorriendo] = useState(false);
  const [logs, setLogs] = useState([]);
  const [nuevaRegion, setNuevaRegion] = useState("");
  const logRef = useRef(null);

  const pasos = [
    `🔎 Iniciando agente — segmento: "${cfg.segmento}"`,
    `📍 Radio: ${cfg.radio} km desde ${cfg.ciudad}`,
    `📋 Páginas Amarillas → buscando...`,
    `   ✅ 6 empresas encontradas`,
    `📋 Guía Oleo → buscando...`,
    `   ✅ 4 empresas encontradas`,
    `📋 Cylex → buscando...`,
    `   ✅ 3 empresas encontradas`,
    `🔍 SerpAPI → buscando...`,
    `   ✅ 5 empresas encontradas`,
    `🤖 Groq → calificando 18 empresas únicas...`,
    `   ⭐ "Constructora Roca SRL" → score 89`,
    `   ⭐ "Metalúrgica Norte SA" → score 84`,
    `   ⚠️  "Talleres Express" → score 52 (descartado)`,
    `   ⭐ "Ingeniería Sur SRL" → score 77`,
    `💾 4 leads nuevos guardados`,
    `✅ Agente finalizado`,
  ];

  const ejecutar = () => {
    setCorriendo(true); setLogs([]);
    let i = 0;
    const iv = setInterval(() => {
      if (i < pasos.length) {
        setLogs(p => [...p, { t: new Date().toLocaleTimeString("es-AR"), m: pasos[i] }]);
        i++;
        setTimeout(() => { if (logRef.current) logRef.current.scrollTop = 9999; }, 30);
      } else {
        clearInterval(iv);
        setCorriendo(false);
      }
    }, 650);
  };

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
      {/* Config agente */}
      <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.12)", borderRadius:14, padding:20 }}>
        <div style={{ fontWeight:700, fontSize:14, color:"#1A2B4A", marginBottom:16 }}>⚙️ Configurar búsqueda</div>
        {[
          { label:"Segmento / Rubro", key:"segmento", placeholder:"ej: construcción" },
          { label:"Ciudad base",      key:"ciudad",   placeholder:"ej: Córdoba" },
          { label:"Radio (km)",       key:"radio",    placeholder:"ej: 40" },
        ].map(f => (
          <div key={f.key} style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:"rgba(26,43,74,0.35)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{f.label}</div>
            <input
              value={cfg[f.key]}
              onChange={e => setCfg(p => ({...p,[f.key]:e.target.value}))}
              placeholder={f.placeholder}
              style={{
                width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.09)",
                borderRadius:8, padding:"9px 12px", color:"#1A2B4A", fontSize:13, outline:"none", boxSizing:"border-box",
              }}
            />
          </div>
        ))}
        <button
          onClick={ejecutar}
          disabled={corriendo}
          style={{
            width:"100%", padding:"11px 0", borderRadius:9, border:"none",
            background: corriendo ? "rgba(255,107,0,0.2)" : "linear-gradient(135deg,#1a1917,#444440)",
            color:"#1A2B4A", fontWeight:700, fontSize:13, cursor: corriendo ? "not-allowed" : "pointer",
          }}
        >{corriendo ? "⏳ Ejecutando..." : "▶ Ejecutar Agente"}</button>

        {logs.length > 0 && (
          <div ref={logRef} style={{
            marginTop:14, background:"rgba(0,0,0,0.4)", borderRadius:8, padding:12,
            fontFamily:"monospace", fontSize:11, maxHeight:160, overflowY:"auto",
          }}>
            {logs.map((l,i) => (
              <div key={i} style={{ color:"rgba(26,43,74,0.65)", marginBottom:3, display:"flex", gap:8 }}>
                <span style={{ color:"rgba(26,43,74,0.2)", minWidth:64 }}>{l.t}</span>
                <span>{l.m}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Regiones */}
      <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.12)", borderRadius:14, padding:20 }}>
        <div style={{ fontWeight:700, fontSize:14, color:"#1A2B4A", marginBottom:16 }}>🗺️ Regiones activas</div>
        {regiones.map(r => (
          <div key={r} style={{
            display:"flex", alignItems:"center", gap:8, padding:"8px 12px",
            background:"rgba(255,107,0,0.08)", border:"1px solid rgba(255,107,0,0.2)",
            borderRadius:8, marginBottom:8,
          }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:"#1A2B4A" }}/>
            <span style={{ fontSize:13, color:"rgba(26,43,74,0.8)" }}>{r}</span>
          </div>
        ))}
        <div style={{ display:"flex", gap:8, marginTop:12 }}>
          <input
            value={nuevaRegion}
            onChange={e => setNuevaRegion(e.target.value)}
            placeholder="Nueva market..."
            onKeyDown={e => {
              if (e.key==="Enter" && nuevaRegion.trim()) {
                onAgregarRegion(nuevaRegion.trim());
                setNuevaRegion("");
              }
            }}
            style={{
              flex:1, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.09)",
              borderRadius:8, padding:"8px 12px", color:"#1A2B4A", fontSize:13, outline:"none",
            }}
          />
          <button
            onClick={() => { if(nuevaRegion.trim()) { onAgregarRegion(nuevaRegion.trim()); setNuevaRegion(""); }}}
            style={{
              padding:"8px 14px", borderRadius:8, border:"none",
              background:"rgba(59,130,246,0.2)", color:"#1A2B4A", cursor:"pointer", fontWeight:700,
            }}
          >+</button>
        </div>
      </div>
    </div>
  );
}

// ── KNOWLEDGE VIEWER ──────────────────────────────────────────────
function KnowledgeViewer({ empresa }) {
  const [data, setData] = useState({ texto:"", chars:0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`https://fl.velqo.io/api/knowledge/${empresa}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [empresa]);

  const limpiar = () => {
    if (!window.confirm("¿Borrar todo el conocimiento de esta empresa?")) return;
    fetch(`https://fl.velqo.io/api/knowledge/${empresa}/clear`, { method:"POST" })
      .then(() => setData({ texto:"", chars:0 }))
      .catch(() => {});
  };

  return (
    <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.12)", borderRadius:14, padding:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A" }}>Conocimiento actual</div>
          <div style={{ fontSize:10, color:"rgba(26,43,74,0.3)", marginTop:2 }}>
            {data.chars > 0 ? `${data.chars} caracteres cargados` : "Sin conocimiento cargado aún"}
          </div>
        </div>
        {data.chars > 0 && (
          <button onClick={limpiar} style={{
            padding:"5px 12px", borderRadius:7, border:"1px solid rgba(239,68,68,0.3)",
            background:"rgba(239,68,68,0.08)", color:"#EF4444", fontSize:11, cursor:"pointer",
          }}>🗑 Limpiar</button>
        )}
      </div>
      {loading && <div style={{ fontSize:12, color:"rgba(26,43,74,0.3)" }}>Loading...</div>}
      {!loading && data.chars === 0 && (
        <div style={{ fontSize:12, color:"rgba(26,43,74,0.2)", textAlign:"center", padding:"20px 0" }}>
          No hay conocimiento cargado. Subí un archivo arriba para empezar.
        </div>
      )}
      {!loading && data.chars > 0 && (
        <textarea
          readOnly
          value={data.texto}
          style={{
            width:"100%", height:200, background:"rgba(0,0,0,0.3)",
            border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
            padding:"10px 12px", color:"rgba(26,43,74,0.6)", fontSize:11,
            resize:"vertical", outline:"none", fontFamily:"monospace",
          }}
        />
      )}
    </div>
  );
}

// ── TAB ACTIVIDAD ────────────────────────────────────────────────
function TabActividad({ leads, esAdmin }) {
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [filtroEmp, setFiltroEmp] = useState("todas");
  const [filtroTipo, setFiltroTipo] = useState("todos");

  const cargar = () => {
    setLoading(true);
    let url = "https://fl.velqo.io/api/actividad?limit=100";
    if (filtroEmp !== "todas") url += `&empresa=${filtroEmp}`;
    fetch(url)
      .then(r => r.json())
      .then(data => { setLogs(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [filtroEmp]);

  const logsFiltrados = filtroTipo === "todos" ? logs : logs.filter(l => l.tipo === filtroTipo);

  const tipoLabel  = { presentacion:"Introduction", "seguimiento":"Follow-up", presupuesto:"Presupuesto", minerva_ok:"Minerva ✓", minerva_vendedor:"Minerva → Vendedor" };
  const tipoColor  = { presentacion:"#3B82F6", "seguimiento":"#8B5CF6", presupuesto:"#F59E0B", minerva_ok:"#10B981", minerva_vendedor:"#1A2B4A" };
  const empColor   = { fl:"#C8972A" };
  const empLabel   = { fl:"FL" };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ fontSize:15, fontWeight:800, color:"#1A2B4A", flex:1 }}>⚡ Actividad del sistema</div>
        <select value={filtroEmp} onChange={e => setFiltroEmp(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"7px 12px", color:"#1A2B4A", fontSize:12, outline:"none",
        }}>
          <option value="todas">All companies</option>
          <option value="aarti">Fernández Lacort</option>
          
          
        </select>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"7px 12px", color:"#1A2B4A", fontSize:12, outline:"none",
        }}>
          <option value="todos">All types</option>
          <option value="presentacion">Introduction</option>
          <option value="seguimiento">Follow-up</option>
          <option value="presupuesto">Presupuesto</option>
          <option value="minerva_ok">Minerva ✓</option>
          <option value="minerva_vendedor">Minerva → Vendedor</option>
        </select>
        <button onClick={cargar} style={{
          padding:"7px 14px", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
          background:"transparent", color:"rgba(26,43,74,0.5)", cursor:"pointer", fontSize:12,
        }}>↻ Actualizar</button>
      </div>

      {/* Tabla de actividad */}
      {loading && <div style={{ fontSize:13, color:"rgba(26,43,74,0.3)", textAlign:"center", padding:40 }}>Loading...</div>}
      {!loading && logsFiltrados.length === 0 && (
        <div style={{ fontSize:13, color:"rgba(26,43,74,0.2)", textAlign:"center", padding:60 }}>
          No activity recorded aún
        </div>
      )}
      {!loading && logsFiltrados.length > 0 && (
        <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden" }}>
          {logsFiltrados.map((log, i) => {
            const lead = leads.find(l => String(l.id) === String(log.lead_id));
            const fecha = new Date(log.creado_en).toLocaleString("es-AR", {
              day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit"
            });
            return (
              <div key={log.id} style={{
                display:"flex", alignItems:"center", gap:12, padding:"11px 16px",
                borderBottom: i < logsFiltrados.length-1 ? "1px solid rgba(255,255,255,0.05)" : "none",
              }}>
                {/* Estado */}
                <span style={{ fontSize:14, minWidth:16 }}>{log.estado === "enviado" ? "✅" : "❌"}</span>

                {/* Tipo */}
                <span style={{
                  fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, whiteSpace:"nowrap",
                  background:`${tipoColor[log.tipo] || "#6B7280"}20`,
                  color: tipoColor[log.tipo] || "#6B7280",
                }}>
                  {tipoLabel[log.tipo] || log.tipo}
                </span>

                {/* Empresa captio */}
                <span style={{
                  fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:10, whiteSpace:"nowrap",
                  background:`${empColor[log.empresa] || "#6B7280"}20`,
                  color: empColor[log.empresa] || "#6B7280",
                }}>
                  {empLabel[log.empresa] || log.empresa}
                </span>

                {/* Lead */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, color:"#1A2B4A", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {lead?.empresa || `Lead #${log.lead_id}`}
                  </div>
                  <div style={{ fontSize:10, color:"rgba(26,43,74,0.35)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {log.destinatario}
                  </div>
                </div>

                {/* Enviado por */}
                {log.enviado_por && (
                  <span style={{ fontSize:10, color:"rgba(26,43,74,0.3)", whiteSpace:"nowrap" }}>
                    👤 {log.enviado_por}
                  </span>
                )}

                {/* Fecha */}
                <span style={{ fontSize:10, color:"rgba(26,43,74,0.25)", whiteSpace:"nowrap" }}>{fecha}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* KPIs rápidos */}
      {!loading && logs.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10, marginTop:16 }}>
          {[
            { label:"Total enviados",     value: logs.filter(l=>l.estado==="enviado").length, color:"#10B981" },
            { label:"Presentaciones",     value: logs.filter(l=>l.tipo==="presentacion").length, color:"#3B82F6" },
            { label:"Follow-up",        value: logs.filter(l=>l.tipo==="seguimiento").length, color:"#8B5CF6" },
            { label:"Errores",            value: logs.filter(l=>l.estado==="error").length, color:"#EF4444" },
          ].map(k => (
            <div key={k.label} style={{
              background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:10, padding:"12px 14px",
            }}>
              <div style={{ fontSize:22, fontWeight:900, color:k.color }}>{k.value}</div>
              <div style={{ fontSize:10, color:"rgba(26,43,74,0.4)", marginTop:4 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── TEMPLATES DEFAULT ────────────────────────────────────────────
const FL_TPL_HEADER = `<tr><td style="padding:40px 40px 20px 40px;text-align:center;border-bottom:1px solid #e0dbd4;">
    <div style="font-size:28px;font-weight:normal;color:#1a1917;letter-spacing:0.08em;">Fernández Lacort</div>
    <div style="font-size:10px;letter-spacing:0.22em;text-transform:uppercase;color:#888884;margin-top:4px;">Intellectual Property · Latin America</div>
  </td></tr>`;
const FL_TPL_FOOTER = `<tr><td style="padding:16px 40px;border-top:1px solid #e0dbd4;text-align:center;">
    <p style="font-size:10px;color:#aaa9a6;margin:0;letter-spacing:0.1em;">fernandezlacort.com.ar · hola@fernandezlacort.com.ar</p>
  </td></tr>`;
const FL_TPL_WRAP = (body) => `<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,'Times New Roman',serif;background:#f4f1ec;border:1px solid #e0dbd4;">
  ${FL_TPL_HEADER}
  <tr><td style="padding:36px 40px 20px 40px;">
    ${body}
  </td></tr>
  ${FL_TPL_FOOTER}
</table>`;

const TEMPLATES_DEFAULT = [
  {
    id: "presentacion",
    nombre: "1. Presentación",
    tipo: "presentacion",
    asunto: "Fernández Lacort — Propiedad Intelectual en Latinoamérica para {{empresa}}",
    html: FL_TPL_WRAP(`<p style="font-size:15px;color:#1a1917;line-height:1.7;margin:0 0 16px 0;">Estimado/a <strong>{{contacto}}</strong>,</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Me pongo en contacto desde <strong>Fernández Lacort</strong>, a law firm specializing in Intellectual Property across Latin America.</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 24px 0;">Ofrecemos servicios de registro de marcas, patentes y asesoramiento integral en PI para empresas que buscan proteger sus activos intangibles en la región.</p>
    <table cellpadding="0" cellspacing="0" style="margin:0 0 28px 0;">
      <tr><td style="background:#1a1917;padding:14px 32px;">
        <a href="mailto:info@fernandezlacort.com.ar" style="color:#f4f1ec;text-decoration:none;font-family:Georgia,serif;font-size:13px;font-style:italic;letter-spacing:0.04em;">Conversemos →</a>
      </td></tr>
    </table>
    <p style="font-size:13px;color:#888884;line-height:1.7;margin:0;">Quedo a su disposición para cualquier consulta.</p>
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">Fernández Lacort Team<br>fernandezlacort.com.ar</p>`),
  },
  {
    id: "reintento1",
    nombre: "2. Reintento — día 30",
    tipo: "reintento1",
    asunto: "Re: Fernández Lacort — Propiedad Intelectual para {{empresa}}",
    html: FL_TPL_WRAP(`<p style="font-size:15px;color:#1a1917;line-height:1.7;margin:0 0 16px 0;">Hola <strong>{{contacto}}</strong>,</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Hace unas semanas le escribí para presentarle <strong>Fernández Lacort</strong>. No quería que se pierda en la bandeja de entrada — sigo a disposición si le interesa charlarlo.</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 24px 0;">¿Le viene bien que le enviemos una propuesta, o prefiere que lo llamemos?</p>
    <table cellpadding="0" cellspacing="0" style="margin:0 0 28px 0;">
      <tr><td style="background:#1a1917;padding:14px 32px;">
        <a href="mailto:info@fernandezlacort.com.ar" style="color:#f4f1ec;text-decoration:none;font-family:Georgia,serif;font-size:13px;font-style:italic;letter-spacing:0.04em;">Conversemos →</a>
      </td></tr>
    </table>
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">Fernández Lacort Team</p>`),
  },
  {
    id: "reintento2",
    nombre: "3. Reintento — día 60",
    tipo: "reintento2",
    asunto: "Protección de marca en Latinoamérica — {{empresa}}",
    html: FL_TPL_WRAP(`<p style="font-size:15px;color:#1a1917;line-height:1.7;margin:0 0 16px 0;">Hola <strong>{{contacto}}</strong>,</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Quería retomar el contacto. Trabajamos con varias empresas de su sector ayudándolas a proteger su propiedad intelectual en Latinoamérica, y creo que podríamos sumarle valor a {{empresa}}.</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 24px 0;">Si es buen momento, me cuenta y avanzamos.</p>
    <table cellpadding="0" cellspacing="0" style="margin:0 0 28px 0;">
      <tr><td style="background:#1a1917;padding:14px 32px;">
        <a href="mailto:info@fernandezlacort.com.ar" style="color:#f4f1ec;text-decoration:none;font-family:Georgia,serif;font-size:13px;font-style:italic;letter-spacing:0.04em;">Conversemos →</a>
      </td></tr>
    </table>
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">Fernández Lacort Team</p>`),
  },
  {
    id: "reintento3",
    nombre: "4. Reintento — día 90 (último)",
    tipo: "reintento3",
    asunto: "Última consulta — Fernández Lacort para {{empresa}}",
    html: FL_TPL_WRAP(`<p style="font-size:15px;color:#1a1917;line-height:1.7;margin:0 0 16px 0;">Hola <strong>{{contacto}}</strong>,</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Quería volver a escribirle por si todavía les puede servir lo que hacemos en <strong>Fernández Lacort</strong>. Si en este momento no es prioridad, no hay problema — quedamos a disposición para cuando lo sea.</p>
    <table cellpadding="0" cellspacing="0" style="margin:0 0 28px 0;">
      <tr><td style="background:#1a1917;padding:14px 32px;">
        <a href="mailto:info@fernandezlacort.com.ar" style="color:#f4f1ec;text-decoration:none;font-family:Georgia,serif;font-size:13px;font-style:italic;letter-spacing:0.04em;">Conversemos →</a>
      </td></tr>
    </table>
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">Fernández Lacort Team</p>`),
  },
  {
    id: "gracias_presupuesto",
    nombre: "5. Gracias — pidió presupuesto",
    tipo: "gracias_presupuesto",
    asunto: "Recibimos su consulta — Fernández Lacort",
    html: FL_TPL_WRAP(`<p style="font-size:15px;color:#1a1917;line-height:1.7;margin:0 0 16px 0;">¡Gracias por su respuesta, <strong>{{contacto}}</strong>!</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Tomamos nota de su interés en recibir una propuesta. En las próximas 24-48hs alguien de nuestro equipo le va a enviar el presupuesto con el detalle de precios.</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Cualquier consulta mientras tanto, escríbanos.</p>
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">Fernández Lacort Team</p>`),
  },
  {
    id: "gracias_contacto",
    nombre: "6. Gracias — pidió que lo contacten",
    tipo: "gracias_contacto",
    asunto: "Gracias por contactarnos — Fernández Lacort",
    html: FL_TPL_WRAP(`<p style="font-size:15px;color:#1a1917;line-height:1.7;margin:0 0 16px 0;">¡Gracias por su respuesta, <strong>{{contacto}}</strong>!</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Tomamos nota — en las próximas 24-48hs alguien de nuestro equipo se va a comunicar con usted para charlar los detalles.</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Que tenga buen día.</p>
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">Fernández Lacort Team</p>`),
  },
  {
    id: "gracias_otro",
    nombre: "7. Gracias — respuesta general",
    tipo: "gracias_otro",
    asunto: "Gracias por su mensaje — Fernández Lacort",
    html: FL_TPL_WRAP(`<p style="font-size:15px;color:#1a1917;line-height:1.7;margin:0 0 16px 0;">¡Gracias por su respuesta, <strong>{{contacto}}</strong>!</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Recibimos su mensaje. En las próximas 24-48hs alguien de nuestro equipo le va a responder personalmente.</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Saludos.</p>
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">Fernández Lacort Team</p>`),
  },
  {
    id: "5preguntas",
    nombre: "8. 5 preguntas calificadoras",
    tipo: "5preguntas",
    asunto: "5 preguntas rápidas — Fernández Lacort para {{empresa}}",
    html: FL_TPL_WRAP(`<p style="font-size:15px;color:#1a1917;line-height:1.7;margin:0 0 16px 0;">Hola <strong>{{contacto}}</strong>,</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">Para poder armarle una propuesta a medida para {{empresa}}, le hago 5 preguntas rápidas:</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 16px 0;">1. ¿En qué países de Latinoamérica tienen presencia o planean expandirse?<br>2. ¿Tienen marcas registradas actualmente?<br>3. ¿Qué tipo de protección necesitan (marcas, patentes, diseños)?<br>4. ¿Cuál es el timeline ideal para avanzar?<br>5. ¿Hay algún competidor que los preocupe en particular?</p>
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0 0 24px 0;">Puede responder este mismo email o, si prefiere, coordinamos una llamada.</p>
    <p style="font-size:13px;color:#444440;margin:16px 0 0 0;font-style:italic;">Fernández Lacort Team</p>`),
  },
];

const TIPOS_EMAIL = [
  { id: "presentacion",         label: "1. Presentación"        },
  { id: "reintento1",           label: "2. Reintento día 30"    },
  { id: "reintento2",           label: "3. Reintento día 60"    },
  { id: "reintento3",           label: "4. Reintento día 90"    },
  { id: "gracias_presupuesto",  label: "5. Gracias (presupuesto)" },
  { id: "gracias_contacto",     label: "6. Gracias (contacto)"  },
  { id: "gracias_otro",         label: "7. Gracias (otro)"      },
  { id: "5preguntas",           label: "8. 5 Preguntas"         },
  { id: "custom",               label: "Personalizado"          },
];

// ── EMAIL SECTION (sub-tabs) ─────────────────────────────────────
function EmailSection({ esAdmin, schedule, setSchedule, leads }) {
  const [subTab, setSubTab] = useState("seguimiento");

  const subtabs = [
    { id: "seguimiento", label: "📊 Seguimiento" },
    { id: "plantillas",  label: "📝 Plantillas" },
  ];

  return (
    <div>
      {/* Toggle envío automático */}
      {esAdmin && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, padding:"12px 16px", background:"rgba(26,43,74,0.02)", border:"1px solid rgba(26,43,74,0.08)", borderRadius:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A" }}>Envío automático de emails</div>
            <div style={{ fontSize:11, color:"rgba(26,43,74,0.4)", marginTop:2 }}>
              {schedule.envio_automatico
                ? "El sistema envía presentaciones y seguimientos automáticamente"
                : "Los emails solo se envían de forma manual desde la ficha del lead"}
            </div>
          </div>
          <button onClick={() => {
            const nuevo = !schedule.envio_automatico;
            if (!window.confirm(nuevo
              ? "¿Activar el envío automático de emails? El sistema enviará presentaciones y seguimientos según la secuencia configurada."
              : "¿Desactivar el envío automático? Los emails solo se podrán enviar manualmente."
            )) return;
            const nuevoSchedule = { ...schedule, envio_automatico: nuevo };
            setSchedule(nuevoSchedule);
            fetch(`https://fl.velqo.io/api/schedule/fl`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(nuevoSchedule),
            }).catch(() => {});
          }} style={{
            padding:"6px 16px", borderRadius:20, border:"none", cursor:"pointer", fontSize:12, fontWeight:600,
            background: schedule.envio_automatico ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
            color: schedule.envio_automatico ? "#10B981" : "#EF4444",
          }}>
            {schedule.envio_automatico ? "● Activado" : "○ Desactivado"}
          </button>
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:20, borderBottom:"1px solid rgba(26,43,74,0.06)", paddingBottom:0 }}>
        {subtabs.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)} style={{
            padding:"10px 20px", fontSize:13, fontWeight: subTab === st.id ? 700 : 500, cursor:"pointer",
            background:"none", border:"none", borderBottom: subTab === st.id ? "2px solid #1A2B4A" : "2px solid transparent",
            color: subTab === st.id ? "#1A2B4A" : "rgba(26,43,74,0.4)",
            marginBottom:-1,
          }}>{st.label}</button>
        ))}
      </div>

      {subTab === "seguimiento" && <TabSeguimiento leads={leads} />}
      {subTab === "plantillas" && <TabEmailEditor />}
    </div>
  );
}

// ── TAB SEGUIMIENTO DE EMAILS ────────────────────────────────────
function TabSeguimiento({ leads }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  const [hiloAbierto, setHiloAbierto] = useState(null);

  useEffect(() => {
    fetch("https://fl.velqo.io/api/seguimiento/fl")
      .then(r => r.json())
      .then(data => { setLogs(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Agrupar logs por lead_id
  const porLead = {};
  logs.forEach(log => {
    const key = log.lead_id;
    if (!porLead[key]) porLead[key] = { lead_id: key, email: log.destinatario, envios: [], estados: new Set() };
    porLead[key].envios.push(log);
    porLead[key].estados.add(log.estado);
    if (log.abierto_en) porLead[key].abierto = true;
  });

  const leadsAgrupados = Object.values(porLead).map(g => {
    const lead = leads.find(l => String(l.id) === String(g.lead_id));
    const ultimo = g.envios[0];
    let estadoFinal = "enviado";
    if (g.estados.has("rebote")) estadoFinal = "rebote";
    else if (g.estados.has("baja")) estadoFinal = "baja";
    else if (lead?.etiqueta_respuesta) estadoFinal = "respondido";
    else if (lead?.estado === "Contestado" || lead?.estado === "Validado") estadoFinal = "respondido";
    else if (g.abierto) estadoFinal = "abierto";
    return { ...g, lead, ultimo, estadoFinal };
  });

  // Métricas
  const total = leadsAgrupados.length;
  const abiertos = leadsAgrupados.filter(l => l.abierto).length;
  const respondidos = leadsAgrupados.filter(l => l.estadoFinal === "respondido").length;
  const bajas = leadsAgrupados.filter(l => l.estadoFinal === "baja").length;
  const rebotes = leadsAgrupados.filter(l => l.estadoFinal === "rebote").length;
  const pctAbiertos = total ? Math.round(abiertos / total * 100) : 0;
  const pctRespondidos = total ? Math.round(respondidos / total * 100) : 0;

  // Filtrar
  let filtrados = leadsAgrupados;
  if (filtro === "abiertos") filtrados = filtrados.filter(l => l.abierto && l.estadoFinal !== "respondido");
  else if (filtro === "sin_abrir") filtrados = filtrados.filter(l => !l.abierto && l.estadoFinal === "enviado");
  else if (filtro === "respondidos") filtrados = filtrados.filter(l => l.estadoFinal === "respondido");
  else if (filtro === "bajas") filtrados = filtrados.filter(l => l.estadoFinal === "baja");
  else if (filtro === "rebotes") filtrados = filtrados.filter(l => l.estadoFinal === "rebote");

  if (busqueda) {
    const q = busqueda.toLowerCase();
    filtrados = filtrados.filter(l =>
      (l.lead?.empresa || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q)
    );
  }

  const estadoBadge = (est) => {
    const map = {
      enviado:    { bg: "rgba(59,130,246,0.1)", color: "#3B82F6", label: "enviado" },
      abierto:    { bg: "rgba(16,185,129,0.1)", color: "#10B981", label: "abierto" },
      respondido: { bg: "rgba(99,102,241,0.1)", color: "#6366F1", label: "respondido" },
      baja:       { bg: "rgba(239,68,68,0.1)",  color: "#EF4444", label: "baja" },
      rebote:     { bg: "rgba(239,68,68,0.1)",  color: "#EF4444", label: "rebote" },
    };
    const s = map[est] || map.enviado;
    return { padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700, background:s.bg, color:s.color, display:"inline-block" };
  };

  const formatFecha = (f) => {
    if (!f) return "";
    const d = new Date(f);
    const ahora = new Date();
    const diff = Math.floor((ahora - d) / 1000);
    const rel = diff < 3600 ? `hace ${Math.floor(diff/60)}m` : diff < 86400 ? `hace ${Math.floor(diff/3600)}h` : `hace ${Math.floor(diff/86400)}d`;
    return { fecha: d.toLocaleDateString("es-ES", { day:"2-digit", month:"short" }) + ", " + d.toLocaleTimeString("es-ES", { hour:"2-digit", minute:"2-digit" }), rel };
  };

  const tipoLabel = { presentacion:"Presentación", reintento1:"Reintento 1", reintento2:"Reintento 2", reintento3:"Reintento 3", gracias_presupuesto:"Gracias (presupuesto)", gracias_contacto:"Gracias (contacto)", gracias_otro:"Gracias (otro)", "5preguntas":"5 Preguntas" };

  if (loading) return <div style={{ padding:60, textAlign:"center", color:"rgba(26,43,74,0.3)", fontSize:13 }}>Cargando seguimiento...</div>;

  // Vista de hilo
  if (hiloAbierto) {
    const grupo = leadsAgrupados.find(l => l.lead_id === hiloAbierto);
    if (!grupo) { setHiloAbierto(null); return null; }
    return (
      <div>
        <button onClick={() => setHiloAbierto(null)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, color:"#C8972A", fontWeight:600, marginBottom:16, padding:0 }}>
          ← Volver al seguimiento
        </button>
        <div style={{ marginBottom:16 }}>
          <div style={{ fontWeight:800, fontSize:18, color:"#1A2B4A" }}>{grupo.lead?.empresa || `Lead #${grupo.lead_id}`}</div>
          <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)" }}>{grupo.email} {grupo.lead?.ciudad ? `· ${grupo.lead.ciudad}` : ""}</div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {[...grupo.envios].reverse().map((log, i) => {
            const f = formatFecha(log.creado_en);
            const abierto = log.abierto_en ? formatFecha(log.abierto_en) : null;
            return (
              <div key={i} style={{ padding:"14px 18px", background:"#fff", border:"1px solid rgba(26,43,74,0.08)", borderRadius:12 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14 }}>{log.estado === "enviado" ? "✅" : log.estado === "rebote" ? "📛" : "❌"}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:"#1A2B4A" }}>{tipoLabel[log.tipo] || log.tipo}</span>
                    <span style={estadoBadge(log.estado)}>{log.estado}</span>
                  </div>
                  <span style={{ fontSize:11, color:"rgba(26,43,74,0.3)" }}>{f.fecha}</span>
                </div>
                {log.asunto && <div style={{ fontSize:11, color:"rgba(26,43,74,0.5)", marginBottom:4 }}>Asunto: {log.asunto}</div>}
                {abierto && <div style={{ fontSize:11, color:"#10B981" }}>👁 Abierto: {abierto.fecha} ({abierto.rel})</div>}
                {log.error_detalle && <div style={{ fontSize:11, color:"#EF4444", marginTop:4 }}>Error: {log.error_detalle}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Métricas */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:12, marginBottom:20 }}>
        {[
          { label:"Contactados", value:total, color:"#1A2B4A", bar:"#1A2B4A" },
          { label:"Abiertos", value:abiertos, pct:pctAbiertos, color:"#10B981", bar:"#10B981" },
          { label:"Respondidos", value:respondidos, pct:pctRespondidos, color:"#6366F1", bar:"#3B82F6" },
          { label:"Bajas", value:bajas, color:"#EF4444", bar:"#EF4444" },
          { label:"Rebotes", value:rebotes, color:"#EF4444", bar:"#EF4444" },
        ].map((m, i) => (
          <div key={i} style={{ padding:"16px 18px", background:"#fff", border:"1px solid rgba(26,43,74,0.08)", borderRadius:12 }}>
            <div style={{ fontSize:11, color:"rgba(26,43,74,0.4)", marginBottom:6 }}>{m.label}</div>
            <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
              <span style={{ fontSize:28, fontWeight:800, color:"#1A2B4A" }}>{m.value}</span>
              {m.pct !== undefined && <span style={{ fontSize:12, color:m.color, fontWeight:600 }}>{m.pct}%</span>}
            </div>
            <div style={{ height:3, background:"rgba(26,43,74,0.06)", borderRadius:2, marginTop:8 }}>
              <div style={{ height:3, borderRadius:2, background:m.bar, width: total ? `${(m.value/total)*100}%` : "0%" }} />
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        {[
          { id:"todos", label:"Todos" },
          { id:"abiertos", label:"👁 Abiertos" },
          { id:"sin_abrir", label:"Sin abrir" },
          { id:"respondidos", label:"💬 Respondidos" },
          { id:"bajas", label:"🚫 Bajas" },
          { id:"rebotes", label:"📛 Rebotes" },
        ].map(f => (
          <button key={f.id} onClick={() => setFiltro(f.id)} style={{
            padding:"6px 14px", borderRadius:20, border: filtro === f.id ? "1px solid #1A2B4A" : "1px solid rgba(26,43,74,0.12)",
            background: filtro === f.id ? "#1A2B4A" : "transparent",
            color: filtro === f.id ? "#F5F4F0" : "rgba(26,43,74,0.5)",
            fontSize:12, fontWeight:600, cursor:"pointer",
          }}>{f.label}</button>
        ))}
        <input
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar empresa / email..."
          style={{ marginLeft:"auto", padding:"7px 14px", borderRadius:8, border:"1px solid rgba(26,43,74,0.1)", background:"#fff", fontSize:12, color:"#1A2B4A", outline:"none", minWidth:200 }}
        />
      </div>

      {/* Tabla */}
      {filtrados.length === 0 && (
        <div style={{ textAlign:"center", padding:60, color:"rgba(26,43,74,0.2)", fontSize:13 }}>Sin resultados</div>
      )}
      {filtrados.length > 0 && (
        <div style={{ border:"1px solid rgba(26,43,74,0.08)", borderRadius:14, overflow:"hidden", background:"#fff" }}>
          {/* Header */}
          <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1fr 80px 60px", padding:"10px 16px", background:"rgba(26,43,74,0.02)", borderBottom:"1px solid rgba(26,43,74,0.06)" }}>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Empresa</span>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Enviado</span>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Estado</span>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"center" }}>Envíos</span>
            <span />
          </div>
          {filtrados.map((g, i) => {
            const f = formatFecha(g.ultimo?.creado_en);
            return (
              <div key={g.lead_id} style={{
                display:"grid", gridTemplateColumns:"2fr 1fr 1fr 80px 60px", padding:"12px 16px", alignItems:"center",
                borderBottom: i < filtrados.length - 1 ? "1px solid rgba(26,43,74,0.05)" : "none",
                cursor:"pointer",
              }} onClick={() => setHiloAbierto(g.lead_id)}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1A2B4A" }}>{g.lead?.empresa || `Lead #${g.lead_id}`}</div>
                  <div style={{ fontSize:11, color:"rgba(26,43,74,0.35)" }}>{g.email} {g.lead?.ciudad ? `· ${g.lead.ciudad}` : ""}</div>
                </div>
                <div>
                  <div style={{ fontSize:12, color:"#1A2B4A" }}>{f.fecha}</div>
                  <div style={{ fontSize:10, color:"rgba(26,43,74,0.3)" }}>{f.rel}</div>
                </div>
                <div><span style={estadoBadge(g.estadoFinal)}>{g.estadoFinal}</span></div>
                <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#1A2B4A" }}>{g.envios.length}</div>
                <div style={{ textAlign:"right", fontSize:12, color:"#C8972A", fontWeight:600 }}>Ver hilo →</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── TAB EMAIL EDITOR ─────────────────────────────────────────────
function TabEmailEditor() {
  const [templates, setTemplates]         = useState(null); // null = cargando
  const [seleccionado, setSeleccionado]   = useState(null);
  const [editando, setEditando]           = useState(null);
  const [vista, setVista]                 = useState("editor");
  const [guardado, setGuardado]           = useState(false);
  const [guardando, setGuardando]         = useState(false);
  const [generando, setGenerando]         = useState(false);
  const [brief, setBrief]                 = useState("");
  const [nuevoNombre, setNuevoNombre]     = useState("");
  const [nuevoTipo, setNuevoTipo]         = useState("presentacion");
  const [creando, setCreando]             = useState(false);
  const [errorCarga, setErrorCarga]       = useState(false);

  // Cargar templates desde el servidor al montar
  useEffect(() => {
    fetch("https://fl.velqo.io/api/templates/fl")
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) && data.length > 0 ? data : TEMPLATES_DEFAULT;
        setTemplates(arr);
        setSeleccionado(arr[0]?.id || null);
      })
      .catch(() => {
        // Sin servidor — usar defaults
        setTemplates(TEMPLATES_DEFAULT);
        setSeleccionado(TEMPLATES_DEFAULT[0]?.id || null);
        setErrorCarga(true);
      });
  }, []);

  // Guardar array completo en el servidor
  const persistir = async (arr) => {
    setGuardando(true);
    try {
      await fetch("https://fl.velqo.io/api/templates/fl", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(arr),
      });
    } catch(e) {
      console.warn("No se pudo guardar en servidor, solo en memoria");
    }
    setGuardando(false);
  };

  const tpl = editando || (templates || []).find(t => t.id === seleccionado);

  const seleccionar = (id) => {
    setSeleccionado(id);
    setEditando(null);
    setVista("editor");
    setGuardado(false);
  };

  const cambiarHtml = (html) => {
    setEditando({ ...tpl, html });
    setGuardado(false);
  };

  const cambiarNombre = (nombre) => {
    setEditando({ ...tpl, nombre });
    setGuardado(false);
  };

  const cambiarTipo = (tipo) => {
    setEditando({ ...tpl, tipo });
    setGuardado(false);
  };

  const cambiarAsunto = (asunto) => {
    setEditando({ ...tpl, asunto });
    setGuardado(false);
  };

  const guardar = async () => {
    const updated = templates.map(t => t.id === tpl.id ? tpl : t);
    await persistir(updated);
    setTemplates(updated);
    setEditando(null);
    setGuardado(true);
    setTimeout(() => setGuardado(false), 2500);
  };

  const descartar = () => {
    setEditando(null);
    setGuardado(false);
  };

  const crearTemplate = async () => {
    if (!nuevoNombre.trim()) return;
    const id = "tpl_" + Date.now();
    const nuevo = {
      id,
      nombre: nuevoNombre.trim(),
      tipo: nuevoTipo,
      html: `<table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,'Times New Roman',serif;background:#f4f1ec;border:1px solid #e0dbd4;">
  <tr><td style="padding:40px 40px 20px 40px;text-align:center;border-bottom:1px solid #e0dbd4;">
    <div style="font-size:28px;font-weight:normal;color:#1a1917;letter-spacing:0.08em;">Fernández Lacort</div>
    <div style="font-size:10px;letter-spacing:0.22em;text-transform:uppercase;color:#888884;margin-top:4px;">Intellectual Property · Latin America</div>
  </td></tr>
  <tr><td style="padding:36px 40px 20px 40px;">
    <p style="font-size:14px;color:#444440;line-height:1.8;margin:0;">Escribí el contenido de tu email aquí...</p>
  </td></tr>
  <tr><td style="padding:16px 40px;border-top:1px solid #e0dbd4;text-align:center;">
    <p style="font-size:10px;color:#aaa9a6;margin:0;letter-spacing:0.1em;">fernandezlacort.com.ar · hola@fernandezlacort.com.ar</p>
  </td></tr>
</table>`,
    };
    const updated = [...templates, nuevo];
    await persistir(updated);
    setTemplates(updated);
    setSeleccionado(id);
    setEditando(null);
    setCreando(false);
    setNuevoNombre("");
    setNuevoTipo("presentacion");
  };

  const eliminarTemplate = async (id) => {
    if (!window.confirm("¿Eliminar este template?")) return;
    const updated = templates.filter(t => t.id !== id);
    await persistir(updated);
    setTemplates(updated);
    setSeleccionado(updated[0]?.id || null);
    setEditando(null);
  };

  const generarConIA = async () => {
    if (!brief.trim()) return;
    setGenerando(true);
    try {
      const resp = await fetch("https://fl.velqo.io/api/ai/generar-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brief }),
      });
      const data = await resp.json();
      const html = data.html || "";
      const limpio = html.replace(/```html?/gi, "").replace(/```/g, "").trim();
      cambiarHtml(limpio);
      setBrief("");
    } catch (e) {
      alert("Error al conectar con la IA");
    }
    setGenerando(false);
  };

  const tipoColor = { presentacion:"#3B82F6", seguimiento:"#F59E0B", propuesta:"#10B981", custom:"#8B5CF6" };

  // Cargando
  if (templates === null) return (
    <div style={{ padding:60, textAlign:"center", color:"rgba(26,43,74,0.3)", fontSize:13 }}>
      Cargando templates...
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:18, color:"#1A2B4A", marginBottom:3 }}>✉️ Editor de emails</div>
          <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)" }}>Creá y editá los templates. Asigná el tipo para que Hermes los use automáticamente.</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {guardando && <span style={{ fontSize:11, color:"rgba(26,43,74,0.4)", fontStyle:"italic" }}>Guardando...</span>}
          {errorCarga && (
            <span style={{ fontSize:11, color:"#F59E0B", background:"rgba(245,158,11,0.1)", padding:"4px 10px", borderRadius:6 }}>
              ⚠️ Sin servidor — cambios solo en memoria
            </span>
          )}
          <button
            onClick={() => setCreando(true)}
            style={{
              padding:"9px 18px", borderRadius:8, border:"1px solid rgba(26,43,74,0.2)",
              background:"#1A2B4A", color:"#F5F4F0", fontSize:12, fontWeight:700, cursor:"pointer",
              fontFamily:"Georgia,serif", fontStyle:"italic",
            }}
          >+ Nuevo template</button>
        </div>
      </div>

      {/* Modal nuevo template */}
      {creando && (
        <div style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.6)",
          display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16,
        }}>
          <div style={{
            background:"#F5F4F0", border:"1px solid rgba(26,43,74,0.12)",
            borderRadius:16, padding:28, width:"100%", maxWidth:400,
          }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#1A2B4A", marginBottom:20 }}>Nuevo template de email</div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Nombre</label>
              <input
                value={nuevoNombre}
                onChange={e => setNuevoNombre(e.target.value)}
                placeholder="ej: Email evento especial"
                style={{
                  width:"100%", background:"#fff", border:"1px solid rgba(26,43,74,0.15)",
                  borderRadius:8, padding:"10px 14px", color:"#1A2B4A", fontSize:13,
                  outline:"none", boxSizing:"border-box",
                }}
              />
            </div>
            <div style={{ marginBottom:22 }}>
              <label style={{ fontSize:11, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Tipo (Hermes lo usa para enviarlo)</label>
              <select
                value={nuevoTipo}
                onChange={e => setNuevoTipo(e.target.value)}
                style={{
                  width:"100%", background:"#fff", border:"1px solid rgba(26,43,74,0.15)",
                  borderRadius:8, padding:"10px 14px", color:"#1A2B4A", fontSize:13,
                  outline:"none", boxSizing:"border-box",
                }}
              >
                {TIPOS_EMAIL.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setCreando(false)} style={{
                flex:1, padding:"10px", borderRadius:8, border:"1px solid rgba(26,43,74,0.12)",
                background:"transparent", color:"rgba(26,43,74,0.5)", cursor:"pointer", fontSize:13,
              }}>Cancelar</button>
              <button
                onClick={crearTemplate}
                disabled={!nuevoNombre.trim()}
                style={{
                  flex:2, padding:"10px", borderRadius:8, border:"none",
                  background: nuevoNombre.trim() ? "#1A2B4A" : "rgba(26,43,74,0.1)",
                  color: nuevoNombre.trim() ? "#F5F4F0" : "rgba(26,43,74,0.3)",
                  fontWeight:700, fontSize:13, cursor: nuevoNombre.trim() ? "pointer" : "not-allowed",
                }}
              >Crear template</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"260px 1fr", gap:16, alignItems:"start" }}>

        {/* Sidebar — lista de templates */}
        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 16px", borderBottom:"1px solid rgba(26,43,74,0.07)" }}>
            <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", color:"rgba(26,43,74,0.4)" }}>Templates ({templates.length})</div>
          </div>
          {templates.map(t => (
            <div
              key={t.id}
              onClick={() => seleccionar(t.id)}
              style={{
                padding:"13px 16px",
                background: seleccionado === t.id ? "rgba(26,43,74,0.06)" : "transparent",
                borderLeft: seleccionado === t.id ? "3px solid #1a1917" : "3px solid transparent",
                cursor:"pointer",
                borderBottom:"1px solid rgba(26,43,74,0.05)",
                transition:"background 0.1s",
              }}
            >
              <div style={{ fontSize:13, fontWeight: seleccionado===t.id ? 700 : 500, color:"#1A2B4A", marginBottom:4 }}>{t.nombre}</div>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{
                  fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10,
                  background:`${tipoColor[t.tipo]||"#6B7280"}18`,
                  color: tipoColor[t.tipo] || "#6B7280",
                }}>{TIPOS_EMAIL.find(x=>x.id===t.tipo)?.label || t.tipo}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Panel principal */}
        {tpl ? (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

            {/* Barra superior */}
            <div style={{
              background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:12, padding:"14px 18px",
              display:"flex", alignItems:"center", gap:12, flexWrap:"wrap",
            }}>
              {/* Nombre editable */}
              <input
                value={tpl.nombre}
                onChange={e => cambiarNombre(e.target.value)}
                style={{
                  flex:1, minWidth:180, background:"transparent",
                  border:"none", borderBottom:"1px solid rgba(26,43,74,0.15)",
                  color:"#1A2B4A", fontSize:15, fontWeight:700, padding:"2px 0",
                  outline:"none", fontFamily:"Georgia,serif",
                }}
              />

              {/* Tipo — desplegable */}
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:11, color:"rgba(26,43,74,0.4)", whiteSpace:"nowrap" }}>Tipo Hermes:</span>
                <select
                  value={tpl.tipo}
                  onChange={e => cambiarTipo(e.target.value)}
                  style={{
                    background:"#fff", border:"1px solid rgba(26,43,74,0.15)",
                    borderRadius:7, padding:"6px 12px", color:"#1A2B4A",
                    fontSize:12, outline:"none", fontWeight:600,
                  }}
                >
                  {TIPOS_EMAIL.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>

              {/* Asunto del email */}
              <div style={{ display:"flex", alignItems:"center", gap:8, flex:"1 0 100%" }}>
                <span style={{ fontSize:11, color:"rgba(26,43,74,0.4)", whiteSpace:"nowrap" }}>Asunto:</span>
                <input
                  value={tpl.asunto||""}
                  onChange={e => cambiarAsunto(e.target.value)}
                  placeholder="Ej: Presentamos Fernández Lacort — Servicios para {{empresa}}"
                  style={{
                    flex:1, background:"transparent",
                    border:"none", borderBottom:"1px solid rgba(26,43,74,0.15)",
                    color:"#1A2B4A", fontSize:13, padding:"2px 0",
                    outline:"none", fontFamily:"Georgia,serif",
                  }}
                />
              </div>

              {/* Vista preview / editor */}
              <div style={{ display:"flex", background:"rgba(26,43,74,0.06)", borderRadius:7, overflow:"hidden" }}>
                {["editor","preview"].map(v => (
                  <button key={v} onClick={() => setVista(v)} style={{
                    padding:"6px 14px", border:"none", cursor:"pointer",
                    background: vista===v ? "#1A2B4A" : "transparent",
                    color: vista===v ? "#F5F4F0" : "rgba(26,43,74,0.5)",
                    fontSize:11, fontWeight:600, transition:"all 0.1s",
                  }}>{v === "editor" ? "✏️ Código" : "👁 Vista previa"}</button>
                ))}
              </div>

              {/* Guardar / descartar */}
              {editando && (
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={descartar} style={{
                    padding:"6px 14px", borderRadius:7, border:"1px solid rgba(26,43,74,0.12)",
                    background:"transparent", color:"rgba(26,43,74,0.5)", cursor:"pointer", fontSize:12,
                  }}>Descartar</button>
                  <button onClick={guardar} disabled={guardando} style={{
                    padding:"6px 16px", borderRadius:7, border:"none",
                    background: guardando ? "rgba(26,43,74,0.4)" : "#1A2B4A",
                    color:"#F5F4F0", fontWeight:700, cursor: guardando ? "not-allowed" : "pointer", fontSize:12,
                  }}>{guardando ? "Guardando..." : "💾 Guardar"}</button>
                </div>
              )}
              {guardado && (
                <span style={{ fontSize:12, color:"#10B981", fontWeight:600 }}>✓ Guardado</span>
              )}

              {/* Eliminar */}
              {!TEMPLATES_DEFAULT.find(d => d.id === tpl.id) && (
                <button onClick={() => eliminarTemplate(tpl.id)} style={{
                  padding:"6px 10px", borderRadius:7, border:"1px solid rgba(239,68,68,0.2)",
                  background:"rgba(239,68,68,0.07)", color:"#EF4444", cursor:"pointer", fontSize:11,
                }}>🗑</button>
              )}
            </div>

            {/* Editor HTML */}
            {vista === "editor" && (
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, overflow:"hidden" }}>
                <div style={{ padding:"10px 16px", borderBottom:"1px solid rgba(26,43,74,0.07)", display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:11, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:"0.1em" }}>HTML del email</span>
                  <span style={{ fontSize:10, color:"rgba(26,43,74,0.25)" }}>· Gmail-compatible · estilos inline · tablas</span>
                </div>
                <textarea
                  value={tpl.html}
                  onChange={e => cambiarHtml(e.target.value)}
                  spellCheck={false}
                  style={{
                    width:"100%", height:360, background:"rgba(0,0,0,0.02)",
                    border:"none", padding:"16px 18px",
                    color:"#1A2B4A", fontSize:12, fontFamily:"'Courier New',monospace",
                    resize:"vertical", outline:"none", boxSizing:"border-box",
                    lineHeight:1.6,
                  }}
                />
              </div>
            )}

            {/* Vista previa */}
            {vista === "preview" && (
              <div style={{ background:"#e8e4de", borderRadius:12, padding:24, overflow:"hidden" }}>
                <div style={{ fontSize:11, color:"rgba(26,43,74,0.4)", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.1em" }}>
                  Vista previa — se verá similar en Gmail
                </div>
                <iframe
                  srcDoc={`<!DOCTYPE html><html><body style="margin:0;padding:0;background:#e8e4de;">${tpl.html}</body></html>`}
                  style={{ width:"100%", border:"none", borderRadius:8, minHeight:420, background:"transparent" }}
                  title="preview"
                  sandbox="allow-same-origin"
                />
              </div>
            )}

            {/* Generador IA */}
            <div style={{
              background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:12, padding:18,
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#1A2B4A", marginBottom:4 }}>✨ Generador con IA</div>
              <div style={{ fontSize:11, color:"rgba(26,43,74,0.4)", marginBottom:12 }}>
                Describí el email que necesitás y DeepSeek lo genera respetando FL brand identity.
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <textarea
                  value={brief}
                  onChange={e => setBrief(e.target.value)}
                  placeholder='ej: "Email para empresas tecnológicas de Madrid, invitándolas a un taller de 5h sobre liderazgo y presencia. Mencionar beneficios concretos para equipos de más de 50 personas."'
                  style={{
                    flex:1, background:"#fff", border:"1px solid rgba(26,43,74,0.15)",
                    borderRadius:8, padding:"10px 14px", color:"#1A2B4A", fontSize:12,
                    resize:"none", outline:"none", minHeight:72, fontFamily:"Georgia,serif",
                    lineHeight:1.6,
                  }}
                />
                <button
                  onClick={generarConIA}
                  disabled={generando || !brief.trim()}
                  style={{
                    padding:"10px 20px", borderRadius:8, border:"none",
                    background: generando || !brief.trim() ? "rgba(26,43,74,0.1)" : "#1A2B4A",
                    color: generando || !brief.trim() ? "rgba(26,43,74,0.3)" : "#F5F4F0",
                    fontWeight:700, fontSize:12, cursor: generando || !brief.trim() ? "not-allowed" : "pointer",
                    whiteSpace:"nowrap", alignSelf:"stretch", fontFamily:"Georgia,serif", fontStyle:"italic",
                  }}
                >
                  {generando ? "⏳ Generando..." : "✨ Generar\ncon IA"}
                </button>
              </div>
              {generando && (
                <div style={{ marginTop:10, fontSize:11, color:"rgba(26,43,74,0.4)", fontStyle:"italic" }}>
                  La IA está generando el email... puede tardar unos segundos.
                </div>
              )}
            </div>

            {/* Info tipo asignado */}
            <div style={{
              background:`${tipoColor[tpl.tipo]||"#6B7280"}0d`,
              border:`1px solid ${tipoColor[tpl.tipo]||"#6B7280"}25`,
              borderRadius:10, padding:"12px 16px",
            }}>
              <div style={{ fontSize:12, fontWeight:700, color: tipoColor[tpl.tipo]||"#6B7280", marginBottom:4 }}>
                📨 Este template está asignado como: <strong>{TIPOS_EMAIL.find(x=>x.id===tpl.tipo)?.label || tpl.tipo}</strong>
              </div>
              <div style={{ fontSize:11, color:"rgba(26,43,74,0.5)" }}>
                Cuando Hermes envíe un email de tipo <code style={{ background:"rgba(0,0,0,0.06)", padding:"1px 6px", borderRadius:4 }}>{tpl.tipo}</code> for FL, Hermes will use this template.
                Para activarlo en el servidor, copiá el HTML y pegalo en <code style={{ background:"rgba(0,0,0,0.06)", padding:"1px 6px", borderRadius:4 }}>templates/aarti/email_{tpl.tipo}.html</code>
              </div>
            </div>

          </div>
        ) : (
          <div style={{ padding:40, textAlign:"center", color:"rgba(26,43,74,0.3)", fontSize:13 }}>
            Seleccioná un template para editarlo
          </div>
        )}

      </div>
    </div>
  );
}

// ── TAB LEADS ────────────────────────────────────────────────────
function TabLeads({ leads }) {
  const [busqueda, setBusqueda]         = useState("");
  const [filtroEstado, setFiltroEstado] = useState("All");
  const [filtroRegion, setFiltroRegion] = useState("All");
  const [sortCol, setSortCol]           = useState("score");
  const [sortDir, setSortDir]           = useState("desc");
  const [importando, setImportando]     = useState(false);
  const [importMsg, setImportMsg]       = useState(null);

  const descargarTemplate = () => {
    const cols = ["empresa","contacto","email","fuente","segmento","ciudad"];
    const ejemplo = ["Acme Corp","John Doe","john@acme.com","Manual","Technology","Buenos Aires"];
    const csv = [cols.join(","), ejemplo.join(",")].join("\n");
    const blob = new Blob(["﻿" + csv], { type:"text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "template_importar_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const importarXLS = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = "";
    setImportando(true);
    setImportMsg(null);
    try {
      const text = await file.text();
      const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) { setImportMsg({ ok:false, msg:"El archivo está vacío o solo tiene encabezados" }); setImportando(false); return; }
      const headers = lines[0].split(",").map(h => h.replace(/"/g,"").trim().toLowerCase());
      const rows = lines.slice(1).map(line => {
        const vals = line.split(",").map(v => v.replace(/"/g,"").trim());
        const obj = {};
        headers.forEach((h,i) => { obj[h] = vals[i] || ""; });
        return obj;
      }).filter(r => r.empresa || r.email);

      const resp = await fetch("https://fl.velqo.io/api/importar-leads", {
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ empresa:"fl", leads:rows }),
      });
      const data = await resp.json();
      if (data.ok) {
        setImportMsg({ ok:true, msg:`${data.importados || rows.length} leads importados correctamente` });
      } else {
        setImportMsg({ ok:false, msg: data.error || "Error al importar" });
      }
    } catch(err) {
      setImportMsg({ ok:false, msg:"Error al procesar el archivo: " + err.message });
    }
    setImportando(false);
  };

  const exportCSV = () => {
    const cols = ["ID","Company","Contact","Role","Email","Phone","City","Market","Segment","Source","Status","Score","Amount","Date","Notes"];
    const rows = leadsFiltrados.map(l => [
      l.id, l.empresa, l.contacto, l.cargo, l.email, l.tel,
      l.ciudad, l.region, l.segmento, l.fuente, l.estado,
      l.score, l.monto, l.fecha, l.notas,
    ]);
    const csv = [cols, ...rows]
      .map(row => row.map(v => '"' + String(v||"").replace(/"/g, '""') + '"').join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type:"text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "leads_fl_" + new Date().toISOString().split("T")[0] + ".csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const estados  = ["All", ...Array.from(new Set(leads.map(l=>l.estado).filter(Boolean)))];
  const regiones = ["All", ...Array.from(new Set(leads.map(l=>l.region).filter(Boolean)))];

  const leadsFiltrados = leads
    .filter(l => {
      if (filtroEstado !== "All" && l.estado !== filtroEstado) return false;
      if (filtroRegion !== "All" && l.region !== filtroRegion) return false;
      if (busqueda && !(l.empresa+l.contacto+l.email+l.segmento).toLowerCase().includes(busqueda.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const va = a[sortCol] ?? "";
      const vb = b[sortCol] ?? "";
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });

  const toggleSort = col => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const thStyle = col => ({
    padding:"10px 12px", textAlign:"left", fontSize:11, fontWeight:700,
    color: sortCol === col ? C.gold : C.navyLite,
    textTransform:"uppercase", letterSpacing:"0.07em", cursor:"pointer",
    borderBottom: "2px solid " + (sortCol === col ? C.gold : C.rule),
    whiteSpace:"nowrap", userSelect:"none", background:"#fff",
  });

  const COLS = [
    { key:"empresa",  label:"Company"  },
    { key:"contacto", label:"Contact"  },
    { key:"email",    label:"Email"    },
    { key:"region",   label:"Market"   },
    { key:"segmento", label:"Segment"  },
    { key:"fuente",   label:"Source"   },
    { key:"estado",   label:"Status"   },
    { key:"score",    label:"Score"    },
    { key:"monto",    label:"Amount"   },
    { key:"fecha",    label:"Date"     },
  ];

  const estadoColor = {
    Nuevo:"#6B7A94", Validado:"#2D4470", Cotizado:C.gold,
    Ganado:"#10B981", Perdido:"#EF4444"
  };

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:18, fontWeight:800, color:C.navy }}>{"\U0001F4CB"} Leads</div>
          <div style={{ fontSize:12, color:C.navyLite, marginTop:2 }}>{leadsFiltrados.length} of {leads.length} leads</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={descargarTemplate}
            style={{ padding:"9px 16px", borderRadius:8, border:"1px solid rgba(26,43,74,0.15)", background:"#fff", color:C.navy, fontWeight:600, fontSize:12, cursor:"pointer" }}>
            📋 Template CSV
          </button>
          <label style={{ padding:"9px 16px", borderRadius:8, border:"1px solid rgba(26,43,74,0.15)", background:"#fff", color:C.navy, fontWeight:600, fontSize:12, cursor: importando ? "not-allowed" : "pointer", opacity: importando ? 0.6 : 1 }}>
            {importando ? "⏳ Importando..." : "📥 Importar CSV"}
            <input type="file" accept=".csv,.txt" style={{ display:"none" }} onChange={importarXLS} disabled={importando} />
          </label>
          <button onClick={exportCSV}
            style={{ padding:"9px 16px", borderRadius:8, border:"1px solid " + C.gold, background:C.gold + "20", color:C.gold, fontWeight:700, fontSize:12, cursor:"pointer" }}>
            📤 Exportar CSV
          </button>
        </div>
      </div>

      {/* Import message banner */}
      {importMsg && (
        <div style={{
          padding:"10px 16px", borderRadius:8, marginBottom:14,
          background: importMsg.ok ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
          border: `1px solid ${importMsg.ok ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)"}`,
          display:"flex", justifyContent:"space-between", alignItems:"center",
        }}>
          <span style={{ fontSize:13, color: importMsg.ok ? "#059669" : "#DC2626", fontWeight:600 }}>
            {importMsg.ok ? "✓" : "✗"} {importMsg.msg}
          </span>
          <button onClick={() => setImportMsg(null)} style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(26,43,74,0.4)", fontSize:16 }}>×</button>
        </div>
      )}

      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Search company, contact, email..."
          style={{ flex:1, minWidth:200, background:"#fff", border:"1px solid " + C.rule, borderRadius:8, padding:"8px 14px", color:C.navy, fontSize:13, outline:"none" }}/>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
          style={{ background:"#fff", border:"1px solid " + C.rule, borderRadius:8, padding:"8px 12px", color:C.navy, fontSize:13, outline:"none" }}>
          {estados.map(e => <option key={e} value={e}>{e === "All" ? "All statuses" : e}</option>)}
        </select>
        <select value={filtroRegion} onChange={e => setFiltroRegion(e.target.value)}
          style={{ background:"#fff", border:"1px solid " + C.rule, borderRadius:8, padding:"8px 12px", color:C.navy, fontSize:13, outline:"none" }}>
          {regiones.map(r => <option key={r} value={r}>{r === "All" ? "All markets" : r}</option>)}
        </select>
      </div>

      <div style={{ background:"#fff", border:"1px solid rgba(26,43,74,0.09)", borderRadius:14, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr>
                {COLS.map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)} style={thStyle(col.key)}>
                    {col.label} {sortCol === col.key ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leadsFiltrados.length === 0 && (
                <tr><td colSpan={COLS.length} style={{ padding:40, textAlign:"center", color:C.navyLite, fontSize:13 }}>No leads found</td></tr>
              )}
              {leadsFiltrados.map((lead, i) => (
                <tr key={lead.id}
                  style={{ borderTop:"1px solid rgba(26,43,74,0.05)", background: i%2===0 ? "#fff" : "#FAFAF9", transition:"background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.gold + "08"}
                  onMouseLeave={e => e.currentTarget.style.background = i%2===0 ? "#fff" : "#FAFAF9"}>
                  <td style={{ padding:"10px 12px", color:C.navy, fontWeight:600, maxWidth:180, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.empresa}</td>
                  <td style={{ padding:"10px 12px", color:C.navyMid }}>
                    <div style={{ fontWeight:500 }}>{lead.contacto}</div>
                    <div style={{ fontSize:10, color:C.navyLite }}>{lead.cargo}</div>
                  </td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:12 }}>{lead.email}</td>
                  <td style={{ padding:"10px 12px" }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:C.gold + "18", color:C.gold, whiteSpace:"nowrap" }}>{lead.region}</span>
                  </td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:12 }}>{lead.segmento}</td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:12 }}>{lead.fuente}</td>
                  <td style={{ padding:"10px 12px" }}>
                    <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:(estadoColor[lead.estado]||"#6B7280") + "18", color:estadoColor[lead.estado]||"#6B7280", whiteSpace:"nowrap" }}>{lead.estado}</span>
                  </td>
                  <td style={{ padding:"10px 12px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:36, height:4, background:"rgba(26,43,74,0.08)", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ width:lead.score + "%", height:"100%", background:scoreColor(lead.score), borderRadius:2 }}/>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, color:scoreColor(lead.score) }}>{lead.score}</span>
                    </div>
                  </td>
                  <td style={{ padding:"10px 12px", color:C.gold, fontWeight:700, fontSize:12, whiteSpace:"nowrap" }}>{lead.monto > 0 ? fmt$(lead.monto) : "—"}</td>
                  <td style={{ padding:"10px 12px", color:C.navyLite, fontSize:11, whiteSpace:"nowrap" }}>{lead.fecha}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {leadsFiltrados.length > 0 && (
          <div style={{ padding:"12px 16px", borderTop:"1px solid rgba(26,43,74,0.06)", display:"flex", gap:24, flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:C.navyLite }}>
              <strong style={{ color:C.navy }}>{leadsFiltrados.length}</strong> leads
            </span>
            <span style={{ fontSize:11, color:C.navyLite }}>
              Avg score: <strong style={{ color:C.navy }}>{Math.round(leadsFiltrados.reduce((a,b)=>a+b.score,0)/leadsFiltrados.length)}</strong>
            </span>
            {leadsFiltrados.some(l=>l.monto>0) && (
              <span style={{ fontSize:11, color:C.navyLite }}>
                Total: <strong style={{ color:C.gold }}>{fmt$(leadsFiltrados.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0))}</strong>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ── APP PRINCIPAL ────────────────────────────────────────────────
export default function App() {
  // ── Estados (TODOS primero) ──────────────────────────────────
  const isMobile = useIsMobile();
  const [usuario, setUsuario]           = useLocalStorage("fl_usuario", null);
  const [leads, setLeads]               = useLocalStorage("fl_leads", LEADS_DEMO);
  const [regiones, setRegiones]         = useLocalStorage("fl_regiones", REGIONES_INICIALES);
  const [tab, setTab]                   = useState("dashboard");
  const [filtroEmpresa, setFiltroEmpresa]     = useState("All");
  const [filtroRegion, setFiltroRegion]       = useState("All");
  const [filtroSegmento, setFiltroSegmento]   = useState("Todos");
  const [filtroVendedor, setFiltroVendedor]   = useState("Todos");
  const [busqueda, setBusqueda]         = useState("");
  const [dragId, setDragId]             = useState(null);
  const [modal, setModal]               = useState(null);
  const [configs, setConfigs]           = useState({});
  const [configEmpresa, setConfigEmpresa]     = useState("aarti");
  const [configEditando, setConfigEditando]   = useState(null);
  const [pendientes, setPendientes]     = useState({});
  const [sysStatus, setSysStatus]       = useState(null);
  const [smtpConfig, setSmtpConfig]     = useState({ email:"", password:"", host:"", port:"", modo:"" });
  const [smtpStatus, setSmtpStatus]     = useState(null);
  const [schedule, setSchedule]         = useState({ dias_busqueda:[1,3], dias_envio:[2,4], hora_envio:"09:00", sistema_activo:false, envio_automatico:false });
  const [estadoSistema, setEstadoSistema] = useState(null);
  const [nuevoVendedorNombre, setNuevoVendedorNombre] = useState("");

  // ── Constantes ───────────────────────────────────────────────
  const [vendedores, setVendedores] = useState(VENDEDORES);
  const esAdmin = usuario?.rol === "admin";

  // ── Hooks de datos ───────────────────────────────────────────
  useLeadsFromFile(setLeads);

  // Fetch vendedores
  useEffect(() => {
    fetch("https://fl.velqo.io/api/vendedores/fl")
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setVendedores(data); })
      .catch(() => {});
  }, []);

  // Fetch SMTP config (admin only)
  useEffect(() => {
    if (!esAdmin) return;
    fetch("https://fl.velqo.io/api/smtp/fl")
      .then(r => r.json())
      .then(data => { if (data && data.email) setSmtpConfig(data); })
      .catch(() => {});
  }, [esAdmin]);

  // Fetch schedule (admin only)
  useEffect(() => {
    if (!esAdmin) return;
    fetch("https://fl.velqo.io/api/schedule/fl")
      .then(r => r.json())
      .then(data => { if (data) setSchedule(prev => ({ ...prev, ...data, envio_automatico: data.envio_automatico || false })); })
      .catch(() => {});
  }, [esAdmin]);

  // Fetch estado-sistema (admin only)
  useEffect(() => {
    if (!esAdmin) return;
    fetch("https://fl.velqo.io/api/estado-sistema/fl")
      .then(r => r.json())
      .then(data => setEstadoSistema(data))
      .catch(() => {});
  }, [esAdmin]);

  useEffect(() => {
    if (!esAdmin) return;
    const empresas = ["fl"];
    empresas.forEach(emp => {
      fetch(`https://fl.velqo.io/api/config/${emp}`)
        .then(r => r.json())
        .then(data => setConfigs(prev => ({ ...prev, [emp]: data })))
        .catch(() => {});
      fetch(`https://fl.velqo.io/api/pendientes/${emp}`)
        .then(r => r.json())
        .then(data => setPendientes(prev => ({ ...prev, [emp]: data.pendientes || 0 })))
        .catch(() => {});
    });
  }, [esAdmin]);

  useEffect(() => {
    const fetchStatus = () => {
      fetch("https://fl.velqo.io/api/status")
        .then(r => r.json())
        .then(data => setSysStatus(data))
        .catch(() => {});
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 2 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // Filtrar leads según rol y filtros
  const leadsFiltrados = leads.filter(l => {
    if (!esAdmin && l.vendedor !== usuario?.id) return false;
    if (filtroEmpresa !== "All" && l.empresa_captio !== filtroEmpresa) return false;
    if (filtroRegion !== "All" && l.region !== filtroRegion) return false;
    if (filtroSegmento !== "Todos" && l.segmento !== filtroSegmento) return false;
    if (filtroVendedor !== "Todos" && l.vendedor !== filtroVendedor) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      return l.empresa.toLowerCase().includes(q) || l.contacto.toLowerCase().includes(q);
    }
    return true;
  });

  // KPIs
  const pipeline    = leads.filter(l=>l.estado==="Cotizado").reduce((a,b)=>a+b.monto,0);
  const ganado      = leads.filter(l=>l.estado==="Ganado").reduce((a,b)=>a+b.monto,0);
  const totalCerr   = leads.filter(l=>l.estado==="Ganado"||l.estado==="Perdido").length;
  const tasaCierre  = totalCerr ? Math.round(leads.filter(l=>l.estado==="Ganado").length/totalCerr*100) : 0;
  const nuevosHoy   = leads.filter(l=>l.fecha===new Date().toISOString().split("T")[0]).length;
  const leadsGanados = leads.filter(l=>l.estado==="Ganado"&&l.monto>0);
  const valorPromedio = leadsGanados.length ? Math.round(leadsGanados.reduce((a,b)=>a+b.monto,0)/leadsGanados.length) : 0;
  const leadsValidados = leads.filter(l=>l.estado==="Validado").length;

  // Drag & Drop
  const handleDragStart = useCallback((e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }, []);

  const handleDrop = useCallback((e, colId) => {
    e.preventDefault();
    if (!dragId) return;
    const lead = leads.find(l => l.id === dragId);
    if (!lead || lead.estado === colId) { setDragId(null); return; }
    setModal({ tipo:"mover", lead, destino:colId });
    setDragId(null);
  }, [dragId, leads]);

  const confirmarMover = ({ monto, motivo }) => {
    const { lead, destino } = modal;
    const nuevosLeads = leads.map(l =>
      l.id === lead.id
        ? { ...l, estado:destino, monto: monto || l.monto, notas: motivo ? `Perdido: ${motivo}` : l.notas }
        : l
    );
    setLeads(nuevosLeads);
    // Persistir en el servidor
    fetch("https://fl.velqo.io/api/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(nuevosLeads.map(l => ({
        ...l,
        cargo_decisor: l.cargo,
        email_estimado: l.email,
        telefono: l.tel,
      })))
    }).catch(() => {});
    setModal(null);
  };

  const asignarVendedor = (leadId, vendedorId) => {
    setLeads(prev => prev.map(l => l.id===leadId ? {...l, vendedor:vendedorId} : l));
  };

  const toggleSistema = () => {
    const nuevoEstado = !schedule.sistema_activo;
    if (!window.confirm(nuevoEstado
      ? "¿Reactivar la búsqueda y el envío automático de emails?"
      : "¿Pausar la búsqueda y el envío automático de emails? El dashboard sigue funcionando normalmente."
    )) return;
    setSchedule(prev => {
      const nuevo = { ...prev, sistema_activo: nuevoEstado };
      fetch(`https://fl.velqo.io/api/schedule/fl`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nuevo),
      }).catch(() => {});
      return nuevo;
    });
  };

  // Segmentos únicos
  const segmentos = ["Todos", ...Array.from(new Set(leads.map(l=>l.segmento).filter(Boolean)))];

  if (!usuario) return <Login vendedores={vendedores} onLogin={setUsuario}/>;

  const tabs = [
    { id:"dashboard",  label:"📊 Dashboard" },
    { id:"kanban",     label:"🗂 Pipeline" },
    { id:"leads",      label:"📋 Leads" },
    { id:"actividad",  label:"⚡ Actividad" },
    { id:"emails",     label:"✉️ Emails" },
    ...(esAdmin ? [
      { id:"config",  label:"🎯 Configuración" },
      { id:"admin",   label:"⚙️ Admin" },
    ] : []),
  ];

  return (
    <div style={{ minHeight:"100vh", background:"#F5F4F0", fontFamily:"'Georgia','Segoe UI',sans-serif", color:"#1A2B4A" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&display=swap');
        *{box-sizing:border-box} ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-thumb{background:rgba(26,43,74,0.15);border-radius:2px}
        select option{background:#f4f1ec}
        @media(max-width:768px){
          .kanban-grid{flex-direction:column!important}
          .kanban-col{width:100%!important;min-width:unset!important}
          .dash-kpis{grid-template-columns:1fr 1fr!important}
          .dash-charts{grid-template-columns:1fr!important}
          .config-grid{grid-template-columns:1fr!important}
          .modal-inner{width:100%!important;max-width:100%!important;height:100%!important;max-height:100%!important;border-radius:0!important;overflow-y:auto}
          .modal-overlay{align-items:flex-end!important;padding:0!important}
          .tabs-bar{overflow-x:auto;-webkit-overflow-scrolling:touch;scrollbar-width:none}
          .tabs-bar::-webkit-scrollbar{display:none}
        }
      `}</style>

      {/* HEADER */}
      <div style={{
        height: isMobile ? 52 : 56,
        padding: isMobile ? "0 16px" : "0 24px",
        borderBottom:"1px solid rgba(26,43,74,0.1)",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        background:"#F5F4F0", position:"sticky", top:0, zIndex:50,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontFamily:"Georgia,serif", fontSize: isMobile ? 16 : 18, fontWeight:"normal", color:"#1A2B4A", letterSpacing:"0.06em" }}>Fernández Lacort</span>
          {!isMobile && <span style={{ fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"#6B7A94" }}>IP · LATAM</span>}
          {nuevosHoy > 0 && (
            <span style={{ fontSize:10, fontWeight:700, background:"rgba(26,43,74,0.08)", color:"#2D4470", padding:"2px 8px", borderRadius:20 }}>
              +{nuevosHoy} hoy
            </span>
          )}
        </div>

        <div className="tabs-bar" style={{ display:"flex", gap:2, maxWidth: isMobile ? "55vw" : "unset" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: isMobile ? "5px 10px" : "5px 14px",
              borderRadius:7, border:"none",
              background: tab===t.id ? "rgba(26,43,74,0.08)" : "transparent",
              color: tab===t.id ? "#1A2B4A" : "#6B7A94",
              fontWeight: tab===t.id ? 700 : 500,
              fontSize: isMobile ? 11 : 12,
              cursor:"pointer", whiteSpace:"nowrap",
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {esAdmin && (
            <button onClick={toggleSistema} title="Pausa/reactiva la búsqueda y el envío automático" style={{
              display:"flex", alignItems:"center", gap:6,
              padding:"4px 10px", borderRadius:20, border:"1px solid rgba(26,43,74,0.12)",
              background: schedule.sistema_activo ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
              color: schedule.sistema_activo ? "#10B981" : "#EF4444",
              cursor:"pointer", fontSize:11, fontWeight:700,
            }}>
              <span style={{ width:7, height:7, borderRadius:"50%", background: schedule.sistema_activo ? "#10B981" : "#EF4444" }}/>
              {isMobile ? "" : (schedule.sistema_activo ? "Sistema activo" : "Sistema pausado")}
            </button>
          )}
          {!isMobile && <span style={{ fontSize:11, fontFamily:"Georgia,serif", fontStyle:"italic", color:"#6B7A94" }}>{usuario.nombre}</span>}
          <button onClick={() => setUsuario(null)} style={{
            padding:"4px 10px", borderRadius:6, border:"1px solid rgba(26,43,74,0.12)",
            background:"transparent", color:"#6B7A94", cursor:"pointer", fontSize:11,
          }}>{isMobile ? "↩" : "Exit"}</button>
        </div>
      </div>

      <div style={{ padding: isMobile ? "12px 10px" : "20px 16px", maxWidth:1400, margin:"0 auto" }}>

        {/* ── LEADS ── */}
        {tab === "leads" && (
          <TabLeads leads={leads}/>
        )}

        {/* ── ACTIVIDAD ── */}
        {tab === "actividad" && (
          <TabActividad leads={leads} esAdmin={esAdmin} />
        )}

        {/* ── EMAILS ── */}
        {tab === "emails" && (
          <EmailSection esAdmin={esAdmin} schedule={schedule} setSchedule={setSchedule} leads={leads} />
        )}

        {/* ── KANBAN ── */}
        {tab === "kanban" && (
          <>
            {/* Filtros */}
            <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
              <input
                value={busqueda}
                onChange={e=>setBusqueda(e.target.value)}
                placeholder="🔍 Buscar empresa..."
                style={{
                  flex:1, minWidth:160, background:"rgba(255,255,255,0.05)",
                  border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
                  padding:"8px 12px", color:"#1A2B4A", fontSize:12, outline:"none",
                }}
              />
              {[
                { val:filtroEmpresa,   set:setFiltroEmpresa,   opts:EMPRESAS_CAPTIO,          label:"Empresa" },
                { val:filtroRegion,    set:setFiltroRegion,    opts:["All",...regiones,"Otros"], label:"Market" },
                { val:filtroSegmento,  set:setFiltroSegmento,  opts:segmentos,                label:"Rubro"  },
                ...(esAdmin ? [{ val:filtroVendedor, set:setFiltroVendedor, opts:["Todos",...vendedores.filter(v=>v.rol==="vendedor").map(v=>v.id)], label:"Vendedor" }] : []),
              ].map(f => (
                <select key={f.label} value={f.val} onChange={e=>f.set(e.target.value)} style={{
                  background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
                  borderRadius:8, padding:"8px 12px", color:"#1A2B4A", fontSize:12, outline:"none",
                }}>
                  {f.opts.map(o => (
                    <option key={o} value={o}>
                      {f.label==="Vendedor" ? (vendedores.find(v=>v.id===o)?.nombre || "Todos") : o}
                    </option>
                  ))}
                </select>
              ))}
            </div>

            {/* Columnas kanban */}
            <div className="kanban-grid" style={{
              display:"flex", gap:10, alignItems:"flex-start",
              overflowX: "auto", WebkitOverflowScrolling:"touch",
              paddingBottom:8,
            }}>
              {COLUMNAS.map(col => (
                <Columna
                  key={col.id}
                  col={col}
                  leads={leadsFiltrados.filter(l=>l.estado===col.id)}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onTap={lead => setModal({ tipo:"detalle", lead })}
                  esAdmin={esAdmin}
                  vendedores={vendedores}
                />
              ))}
            </div>
          </>
        )}

        {/* ── DASHBOARD NASA ── */}
        {tab === "dashboard" && (
          <div>

            {/* FILA 1 — KPIs originales */}
            <div className="dash-kpis" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:20 }}>
              {[
                { icon:"🆕", label:"Leads totales",    value:leads.length,                                                              color:"#8B5CF6", sub:"en el sistema" },
                { icon:"✅", label:"Validados",         value:leadsValidados,                                                           color:"#3B82F6", sub:"con interés confirmado" },
                { icon:"💬", label:"Cotizados",         value:leads.filter(l=>l.estado==="Cotizado").length,                           color:"#F59E0B", sub:"esperando respuesta" },
                { icon:"🏆", label:"Ganados",           value:leads.filter(l=>l.estado==="Ganado").length,                             color:"#10B981", sub:"cierres exitosos" },
                { icon:"💰", label:"Pipeline $",        value:fmt$(pipeline),                                                           color:"#F59E0B", sub:"en cotizaciones" },
                { icon:"📈", label:"Closing rate",      value:`${tasaCierre}%`,                                                        color:"#1A2B4A", sub:"tasa de cierre" },
                { icon:"⭐", label:"Valor promedio",    value:fmt$(valorPromedio),                                                     color:"#10B981", sub:"por operación ganada" },
                { icon:"🔥", label:"Nuevos hoy",        value:nuevosHoy,                                                               color:"#EF4444", sub:"leads del día" },
              ].map(k => (
                <div key={k.label} style={{
                  background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)",
                  borderRadius:12, padding:"14px 16px", position:"relative", overflow:"hidden",
                }}>
                  <div style={{ position:"absolute", top:8, right:10, fontSize:20, opacity:0.15 }}>{k.icon}</div>
                  <div style={{ fontSize:24, fontWeight:900, color:k.color, lineHeight:1 }}>{k.value}</div>
                  <div style={{ fontSize:11, fontWeight:700, color:"rgba(26,43,74,0.7)", marginTop:6 }}>{k.label}</div>
                  <div style={{ fontSize:10, color:"rgba(26,43,74,0.3)", marginTop:2 }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* FILA 2 — Leads calientes + Actividad reciente */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>

              {/* Top leads por score — accionables */}
              <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:14, padding:20 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A", marginBottom:4 }}>🔥 Leads calientes</div>
                <div style={{ fontSize:10, color:"rgba(26,43,74,0.35)", marginBottom:14 }}>Mejores oportunidades sin contactar · hacé clic para actuar</div>
                {[...leads]
                  .filter(l=>["Nuevo","Validado"].includes(l.estado))
                  .sort((a,b)=>b.score-a.score)
                  .slice(0,7)
                  .map(l => (
                    <div key={l.id} style={{
                      display:"flex", alignItems:"center", gap:10, padding:"9px 12px",
                      background:"rgba(26,43,74,0.02)", border:"1px solid rgba(26,43,74,0.06)",
                      borderRadius:8, marginBottom:6, cursor:"pointer",
                    }} onClick={() => setModal({ tipo:"detalle", lead:l })}>
                      <div style={{
                        width:36, height:36, borderRadius:8, flexShrink:0,
                        background: l.score>=85?"rgba(16,185,129,0.12)":l.score>=70?"rgba(245,158,11,0.12)":"rgba(107,114,128,0.1)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:13, fontWeight:900,
                        color: l.score>=85?"#10B981":l.score>=70?"#F59E0B":"#6B7280",
                      }}>{l.score}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:"#1A2B4A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.empresa}</div>
                        <div style={{ fontSize:10, color:"rgba(26,43,74,0.4)" }}>{l.cargo_decisor||l.segmento} · {l.region}</div>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                        <span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background: l.estado==="Validado"?"rgba(59,130,246,0.1)":"rgba(107,114,128,0.1)", color: l.estado==="Validado"?"#3B82F6":"#6B7280" }}>{l.estado}</span>
                        {l.nombre_linkedin && <span style={{ fontSize:9, color:"#0077B5" }}>👤 {l.nombre_linkedin.split(" ")[0]}</span>}
                      </div>
                    </div>
                  ))}
                {leads.filter(l=>["Nuevo","Validado"].includes(l.estado)).length===0 && (
                  <div style={{ fontSize:12, color:"rgba(26,43,74,0.3)", textAlign:"center", padding:"20px 0" }}>No hay leads pendientes de contactar</div>
                )}
              </div>

              {/* Actividad reciente — emails enviados */}
              <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:14, padding:20 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A", marginBottom:4 }}>📨 Actividad reciente</div>
                <div style={{ fontSize:10, color:"rgba(26,43,74,0.35)", marginBottom:14 }}>Últimas acciones del sistema</div>
                {(() => {
                  const recientes = [...leads]
                    .filter(l=>l.estado_email||l.fecha)
                    .sort((a,b)=>new Date(b.fecha||0)-new Date(a.fecha||0))
                    .slice(0,8);
                  if(recientes.length===0) return (
                    <div style={{ fontSize:12, color:"rgba(26,43,74,0.3)", textAlign:"center", padding:"20px 0" }}>Sin actividad registrada</div>
                  );
                  return recientes.map(l => {
                    const tipoEmail = l.estado_email?.replace("_"," ").replace("enviado","") || "";
                    const icon = l.estado==="Ganado"?"🏆":l.estado==="Cotizado"?"📄":l.estado_email?"✉️":"🆕";
                    return (
                      <div key={l.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"1px solid rgba(26,43,74,0.05)", cursor:"pointer" }}
                        onClick={() => setModal({ tipo:"detalle", lead:l })}>
                        <div style={{ fontSize:16, flexShrink:0 }}>{icon}</div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:12, fontWeight:600, color:"#1A2B4A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.empresa}</div>
                          <div style={{ fontSize:10, color:"rgba(26,43,74,0.35)" }}>
                            {l.estado_email ? `Email ${tipoEmail}` : `Lead ${l.estado}`} · {l.region}
                          </div>
                        </div>
                        <div style={{ fontSize:10, color:"rgba(26,43,74,0.3)", flexShrink:0 }}>{l.fecha?.slice(5)||""}</div>
                      </div>
                    );
                  });
                })()}
              </div>

            </div>

            {/* FILA 3 — Cotizaciones abiertas + Estado del sistema */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>

              {/* Cotizaciones abiertas */}
              <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:14, padding:20 }}>
                <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A", marginBottom:4 }}>📄 Cotizaciones abiertas</div>
                <div style={{ fontSize:10, color:"rgba(26,43,74,0.35)", marginBottom:14 }}>
                  Esperando respuesta · Total: {fmt$(leads.filter(l=>l.estado==="Cotizado"&&l.monto>0).reduce((a,b)=>a+b.monto,0))}
                </div>
                {leads.filter(l=>l.estado==="Cotizado").length===0 && (
                  <div style={{ fontSize:12, color:"rgba(26,43,74,0.3)", textAlign:"center", padding:"20px 0" }}>No hay cotizaciones abiertas</div>
                )}
                {leads.filter(l=>l.estado==="Cotizado").sort((a,b)=>b.monto-a.monto).slice(0,6).map(l => (
                  <div key={l.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 0", borderBottom:"1px solid rgba(26,43,74,0.05)", cursor:"pointer" }}
                    onClick={() => setModal({ tipo:"detalle", lead:l })}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#1A2B4A", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.empresa}</div>
                      <div style={{ fontSize:10, color:"rgba(26,43,74,0.35)" }}>{l.contacto||l.cargo_decisor} · {l.region}</div>
                    </div>
                    <div style={{ fontSize:14, fontWeight:800, color:"#C8972A", flexShrink:0 }}>
                      {l.monto>0?fmt$(l.monto):"—"}
                    </div>
                  </div>
                ))}
              </div>

              {/* Estado del sistema */}
              {sysStatus && (
                <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:14, padding:20 }}>
                  <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A", marginBottom:4 }}>⚙️ Sistema operativo</div>
                  <div style={{ fontSize:10, color:"rgba(26,43,74,0.35)", marginBottom:14 }}>Estado de los agentes Captio</div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                    {Object.entries(sysStatus.procesos)
                      .filter(([nombre]) => nombre.includes("fl") || nombre === "fl-api")
                      .map(([nombre, estado]) => (
                        <div key={nombre} style={{
                          display:"flex", alignItems:"center", gap:6, padding:"6px 12px", borderRadius:20,
                          background: estado.ok?"rgba(16,185,129,0.08)":"rgba(245,158,11,0.08)",
                          border:"1px solid "+(estado.ok?"rgba(16,185,129,0.2)":"rgba(245,158,11,0.2)"),
                        }}>
                          <div style={{ width:6, height:6, borderRadius:"50%", background:estado.ok?"#10B981":"#F59E0B", flexShrink:0 }}/>
                          <span style={{ fontSize:11, color:estado.ok?"#10B981":"#F59E0B", fontWeight:600 }}>{nombre}</span>
                        </div>
                      ))}
                  </div>
                  <div style={{ marginTop:16, padding:"12px 14px", background:"rgba(26,43,74,0.03)", borderRadius:10 }}>
                    <div style={{ fontSize:11, color:"rgba(26,43,74,0.5)", marginBottom:8 }}>Próximas búsquedas</div>
                    <div style={{ fontSize:12, color:"#1A2B4A" }}>🔍 buscar-fl · Lun–Vie 9am UTC</div>
                    <div style={{ fontSize:12, color:"#1A2B4A", marginTop:4 }}>🤖 calificar-fl · Lun–Vie 11am UTC</div>
                  </div>
                </div>
              )}

            </div>

          </div>
        )}

        {/* ── CONFIGURACIÓN ── */}
        {tab === "config" && esAdmin && (() => {
          const cfg = configEditando || configs[configEmpresa] || {};
          const empColor = "#1A2B4A";

          const guardarConfig = () => {
            fetch(`https://fl.velqo.io/api/config/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(cfg, null, 2),
            }).then(() => {
              setConfigs(prev => ({ ...prev, [configEmpresa]: cfg }));
              setConfigEditando(null);
              alert("✅ Configuración guardada");
            }).catch(() => alert("❌ Error al guardar"));
          };

          const DIAS = ["D","L","M","X","J","V","S"];
          const MAX_DIAS_PLAN = 2;

          const toggleDia = (key, idx) => {
            const arr = [...(schedule[key] || [])];
            const pos = arr.indexOf(idx);
            if (pos >= 0) {
              arr.splice(pos, 1);
            } else {
              if (arr.length >= MAX_DIAS_PLAN) {
                alert(`Tu plan permite hasta ${MAX_DIAS_PLAN} días para esta acción. Para más volumen, hay que pasar a un plan superior.`);
                return;
              }
              arr.push(idx);
            }
            setSchedule(prev => ({ ...prev, [key]: arr.sort() }));
          };

          const guardarSchedule = () => {
            fetch(`https://fl.velqo.io/api/schedule/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(schedule),
            }).then(() => alert("✅ Horario guardado"))
              .catch(() => alert("❌ Error al guardar horario"));
          };

          const MAX_VENDEDORES_PLAN = 3;

          const generarPin = () => String(Math.floor(1000 + Math.random()*9000));

          const persistirVendedores = (lista) => {
            fetch(`https://fl.velqo.io/api/vendedores/fl`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(lista),
            }).catch(() => alert("❌ Error al guardar usuarios"));
          };

          const agregarVendedor = () => {
            const nombre = nuevoVendedorNombre.trim();
            if (!nombre) return;
            if (vendedores.length >= MAX_VENDEDORES_PLAN) return;
            const nuevo = {
              id: `v${Date.now()}`,
              nombre,
              pin: generarPin(),
              rol: "vendedor",
            };
            const lista = [...vendedores, nuevo];
            setVendedores(lista);
            persistirVendedores(lista);
            setNuevoVendedorNombre("");
          };

          const eliminarVendedor = (id) => {
            if (!window.confirm("¿Eliminar este usuario? No va a poder volver a entrar.")) return;
            const lista = vendedores.filter(v => v.id !== id);
            setVendedores(lista);
            persistirVendedores(lista);
          };

          const PROVEEDORES_SMTP = [
            { label:"Gmail",         host:"smtp.gmail.com",      port:"465", modo:"ssl"      },
            { label:"IONOS",         host:"smtp.ionos.es",       port:"587", modo:"starttls" },
            { label:"Outlook",       host:"smtp.office365.com",  port:"587", modo:"starttls" },
            { label:"Yahoo",         host:"smtp.mail.yahoo.com", port:"465", modo:"ssl"      },
            { label:"Hostinger",     host:"smtp.hostinger.com",  port:"465", modo:"ssl"      },
            { label:"OVH",           host:"ssl0.ovh.net",        port:"465", modo:"ssl"      },
            { label:"Otro / manual", host:"",                    port:"587", modo:"starttls" },
          ];

          const smtpAutoDetectado = (() => {
            if (!smtpConfig.email || !smtpConfig.email.includes("@")) return null;
            const dom = smtpConfig.email.split("@")[1].toLowerCase();
            if (dom === "gmail.com")                               return PROVEEDORES_SMTP[0];
            if (dom.includes("outlook")||dom.includes("hotmail"))  return PROVEEDORES_SMTP[2];
            if (dom === "yahoo.com")                               return PROVEEDORES_SMTP[3];
            return null;
          })();

          const smtpDetectado = smtpAutoDetectado || PROVEEDORES_SMTP.find(p => p.host === smtpConfig.host) || null;

          const guardarSmtp = () => {
            setSmtpStatus("saving");
            const payload = { ...smtpConfig, ...(smtpDetectado||{}) };
            fetch(`https://fl.velqo.io/api/smtp/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(payload),
            }).then(r => r.json())
              .then(d => setSmtpStatus(d.ok ? "ok" : "error"))
              .catch(() => setSmtpStatus("error"));
          };

          const probarSmtp = () => {
            setSmtpStatus("testing");
            const payload = { ...smtpConfig, ...(smtpDetectado||{}) };
            fetch(`https://fl.velqo.io/api/smtp/${configEmpresa}/test`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(payload),
            }).then(r => r.json())
              .then(d => setSmtpStatus(d.ok ? "ok" : "error"))
              .catch(() => setSmtpStatus("error"));
          };

          const sCard = {
            background:"#fff", border:"1px solid rgba(26,43,74,0.1)",
            borderRadius:14, padding:20, marginBottom:16,
          };
          const sLabel = { fontSize:11, color:"rgba(26,43,74,0.4)", marginBottom:5, display:"block" };
          const sInput = {
            width:"100%", border:"1px solid rgba(26,43,74,0.12)", borderRadius:8,
            padding:"8px 12px", fontSize:13, color:"#1A2B4A", outline:"none",
            background:"#f8f7f4", boxSizing:"border-box",
          };
          const sBtn = (primary) => ({
            padding:"9px 18px", borderRadius:8, border: primary ? "none" : "1px solid rgba(26,43,74,0.2)",
            background: primary ? "#1A2B4A" : "transparent",
            color: primary ? "#F5F4F0" : "#1A2B4A",
            fontWeight:600, fontSize:13, cursor:"pointer",
          });

          const PAISES_DISPONIBLES = ["LATAM","Argentina","Brasil","Chile","Colombia","México","Francia","Italia","Alemania","España","China","EEUU"];

          const paisesActivos = cfg.paises || ["LATAM"];

          const togglePais = (p) => {
            const arr = paisesActivos.includes(p) ? paisesActivos.filter(x=>x!==p) : [...paisesActivos, p];
            setConfigEditando({...cfg, paises: arr});
          };

          return (
            <div style={{ maxWidth:"100%" }}>
              <div style={{ marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontWeight:800, fontSize:18, color:"#1A2B4A", marginBottom:4 }}>⚙️ Configuración</div>
                  <div style={{ fontSize:12, color:"rgba(26,43,74,0.35)" }}>Todo lo que el agente necesita para trabajar de forma autónoma</div>
                </div>
                {/* Semáforo de estado del sistema */}
                <div style={{ background:"rgba(26,43,74,0.03)", border:"1px solid rgba(26,43,74,0.08)", borderRadius:10, padding:"10px 14px", minWidth:200 }}>
                  <div style={{ fontWeight:700, fontSize:11, color:"rgba(26,43,74,0.5)", marginBottom:8, textTransform:"uppercase", letterSpacing:0.5 }}>Estado del sistema</div>
                  {estadoSistema ? (
                    <>
                      {["envios","respuestas","busqueda","calificacion"].map(motor => {
                        const info = estadoSistema[motor];
                        if (!info) return (
                          <div key={motor} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                            <span style={{ width:8, height:8, borderRadius:"50%", background:"#D1D5DB", display:"inline-block" }} />
                            <span style={{ fontSize:11, color:"rgba(26,43,74,0.4)" }}>{motor}: sin datos</span>
                          </div>
                        );
                        const horas = (Date.now() - new Date(info.fecha).getTime()) / 3600000;
                        const color = !info.ok ? "#EF4444" : horas > 48 ? "#F59E0B" : "#10B981";
                        const label = !info.ok ? "error" : horas > 48 ? "inactivo" : "ok";
                        return (
                          <div key={motor} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                            <span style={{ width:8, height:8, borderRadius:"50%", background:color, display:"inline-block", boxShadow:`0 0 4px ${color}` }} />
                            <span style={{ fontSize:11, color:"#1A2B4A" }}>{motor}</span>
                            <span style={{ fontSize:10, color:"rgba(26,43,74,0.4)", marginLeft:"auto" }}>{label}</span>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div style={{ fontSize:11, color:"rgba(26,43,74,0.35)" }}>Cargando...</div>
                  )}
                </div>
              </div>

              {/* ── FILA 1: SEGMENTOS + EMAIL ── */}
              <div className="config-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>

                {/* SECCIÓN 1: SEGMENTOS */}
                <div style={{...sCard, marginBottom:0}}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#1A2B4A", marginBottom:4 }}>1. Segmentos de búsqueda</div>
                  <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)", marginBottom:14 }}>Dónde y a quién busca el agente.</div>

                  {/* Países */}
                  <label style={sLabel}>PAÍSES</label>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
                    {PAISES_DISPONIBLES.map(p => (
                      <button key={p} onClick={() => togglePais(p)} style={{
                        padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer", fontWeight:500,
                        border: paisesActivos.includes(p) ? "none" : "1px solid rgba(26,43,74,0.15)",
                        background: paisesActivos.includes(p) ? "#1A2B4A" : "transparent",
                        color: paisesActivos.includes(p) ? "#F5F4F0" : "rgba(26,43,74,0.45)",
                      }}>{p}</button>
                    ))}
                  </div>

                  {/* Sectores */}
                  <label style={sLabel}>SECTORES — el agente rota entre ellos</label>
                  {(cfg.segmentos||[]).map((seg, si) => (
                    <div key={si} style={{ display:"flex", gap:6, marginBottom:6 }}>
                      <input value={seg} onChange={e => { const s=[...(cfg.segmentos||[])]; s[si]=e.target.value; setConfigEditando({...cfg,segmentos:s}); }} style={{...sInput, flex:1}} />
                      <button onClick={() => { const s=(cfg.segmentos||[]).filter((_,i)=>i!==si); setConfigEditando({...cfg,segmentos:s}); }} style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:18, padding:"0 4px" }}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setConfigEditando({...cfg, segmentos:[...(cfg.segmentos||[]),""] })}
                    style={{ marginTop:2, marginBottom:14, padding:"4px 12px", borderRadius:6, border:"1px dashed rgba(26,43,74,0.25)", background:"none", color:"rgba(26,43,74,0.5)", cursor:"pointer", fontSize:12 }}>+ Agregar sector</button>

                  {/* Ciudades */}
                  <label style={sLabel}>CIUDADES — el agente rota entre ellas</label>
                  {(cfg.regiones||[]).map((reg, ri) => (
                    <div key={ri} style={{ display:"flex", gap:6, marginBottom:6 }}>
                      <input value={reg} onChange={e => { const r=[...(cfg.regiones||[])]; r[ri]=e.target.value; setConfigEditando({...cfg,regiones:r}); }} style={{...sInput, flex:1}} />
                      <button onClick={() => { const r=(cfg.regiones||[]).filter((_,i)=>i!==ri); setConfigEditando({...cfg,regiones:r}); }} style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:18, padding:"0 4px" }}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setConfigEditando({...cfg, regiones:[...(cfg.regiones||[]),""] })}
                    style={{ marginTop:2, marginBottom:14, padding:"4px 12px", borderRadius:6, border:"1px dashed rgba(26,43,74,0.25)", background:"none", color:"rgba(26,43,74,0.5)", cursor:"pointer", fontSize:12 }}>+ Agregar ciudad</button>

                  {/* Score + Decisor */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                    <div>
                      <label style={sLabel}>Score mínimo</label>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <input type="range" min="0" max="100" value={cfg.score_minimo||65}
                          onChange={e => setConfigEditando({...cfg, score_minimo:parseInt(e.target.value)})} style={{ flex:1 }} />
                        <span style={{ fontWeight:700, fontSize:15, color:"#1A2B4A", minWidth:28 }}>{cfg.score_minimo||65}</span>
                      </div>
                    </div>
                    <div>
                      <label style={sLabel}>Decisor objetivo</label>
                      <input value={cfg.decisor||""} onChange={e => setConfigEditando({...cfg,decisor:e.target.value})} style={sInput} />
                    </div>
                  </div>

                  <div style={{ marginBottom:12 }}>
                    <label style={sLabel}>Descripción del servicio</label>
                    <textarea value={cfg.servicio||""} onChange={e => setConfigEditando({...cfg,servicio:e.target.value})}
                      style={{...sInput, minHeight:52, resize:"vertical"}} />
                  </div>

                  {configEditando && (
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={guardarConfig} style={sBtn(true)}>💾 Guardar</button>
                      <button onClick={() => setConfigEditando(null)} style={sBtn(false)}>Cancelar</button>
                    </div>
                  )}
                </div>

                {/* SECCIÓN 2+3: DÍAS DE BÚSQUEDA + EMAIL SMTP */}
                <div style={{...sCard, marginBottom:0}}>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#1A2B4A", marginBottom:4 }}>2. Días de búsqueda</div>
                    <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)", marginBottom:4 }}>El agente buscará nuevas empresas estos días.</div>
                    <div style={{ fontSize:11, color:"rgba(26,43,74,0.35)", marginBottom:12, fontStyle:"italic" }}>Plan base: hasta {MAX_DIAS_PLAN} días. Más volumen requiere upgrade.</div>
                    <div style={{ display:"flex", gap:6, marginBottom:14 }}>
                      {DIAS.map((d,i) => {
                        const activo = (schedule.dias_busqueda||[]).includes(i);
                        const bloqueado = !activo && (schedule.dias_busqueda||[]).length >= MAX_DIAS_PLAN;
                        return (
                        <button key={i} onClick={() => toggleDia("dias_busqueda", i)} style={{
                          width:36, height:36, borderRadius:"50%", border:"1px solid rgba(26,43,74,0.15)",
                          cursor: bloqueado ? "not-allowed" : "pointer", fontSize:12, fontWeight:600,
                          background: activo ? "#1A2B4A" : "transparent",
                          color: activo ? "#F5F4F0" : bloqueado ? "rgba(26,43,74,0.2)" : "rgba(26,43,74,0.5)",
                          opacity: bloqueado ? 0.5 : 1,
                        }}>{d}</button>
                      );})}
                    </div>
                    <button onClick={guardarSchedule} style={sBtn(true)}>💾 Guardar horario</button>
                  </div>

                  <div style={{ borderTop:"1px solid rgba(26,43,74,0.08)", paddingTop:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#1A2B4A", marginBottom:4 }}>3. Configuración de email</div>
                    <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)", marginBottom:14 }}>Desde dónde salen los emails. SMTP detectado automáticamente.</div>

                    {/* Selector de proveedor */}
                    <div style={{ marginBottom:12 }}>
                      <label style={sLabel}>PROVEEDOR</label>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {PROVEEDORES_SMTP.map(p => (
                          <button key={p.label} onClick={() => setSmtpConfig(prev => ({...prev, host:p.host, port:p.port, modo:p.modo}))}
                            style={{ padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer", fontWeight:500,
                              border: (smtpConfig.host===p.host||(p.label==="Otro / manual"&&!PROVEEDORES_SMTP.slice(0,-1).find(x=>x.host===smtpConfig.host))) ? "none" : "1px solid rgba(26,43,74,0.15)",
                              background: (smtpConfig.host===p.host||(p.label==="Otro / manual"&&!PROVEEDORES_SMTP.slice(0,-1).find(x=>x.host===smtpConfig.host))) ? "#1A2B4A" : "transparent",
                              color: (smtpConfig.host===p.host||(p.label==="Otro / manual"&&!PROVEEDORES_SMTP.slice(0,-1).find(x=>x.host===smtpConfig.host))) ? "#F5F4F0" : "rgba(26,43,74,0.45)",
                            }}>{p.label}</button>
                        ))}
                      </div>
                    </div>

                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                      <div>
                        <label style={sLabel}>Email remitente</label>
                        <input type="email" value={smtpConfig.email||""} placeholder="hola@fernandezlacort.com.ar"
                          onChange={e => { setSmtpConfig(p=>({...p,email:e.target.value})); setSmtpStatus(null); }} style={sInput} />
                      </div>
                      <div>
                        <label style={sLabel}>Contraseña</label>
                        <input type="password" value={smtpConfig.password||""} placeholder="••••••••••••"
                          onChange={e => { setSmtpConfig(p=>({...p,password:e.target.value})); setSmtpStatus(null); }} style={sInput} />
                        {smtpConfig.host==="smtp.gmail.com" && (
                          <div style={{ marginTop:6, padding:"8px 10px", borderRadius:7, background:"rgba(234,179,8,0.08)", border:"1px solid rgba(234,179,8,0.25)", fontSize:11, color:"#92400e", lineHeight:1.5 }}>
                            ⚠️ <strong>Gmail requiere una contraseña de aplicación</strong>, no tu contraseña normal.<br/>
                            1. Activá la verificación en dos pasos en tu cuenta Google<br/>
                            2. Andá a <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{ color:"#92400e" }}>myaccount.google.com/apppasswords</a><br/>
                            3. Creá una clave para "Captio" y pegala acá (16 caracteres sin espacios)
                          </div>
                        )}
                        {smtpConfig.host==="smtp.ionos.es" && (
                          <div style={{ marginTop:6, padding:"8px 10px", borderRadius:7, background:"rgba(59,130,246,0.06)", border:"1px solid rgba(59,130,246,0.2)", fontSize:11, color:"#1e40af", lineHeight:1.5 }}>
                            ℹ️ Usá la contraseña normal de tu cuenta IONOS.
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={sLabel}>Nombre del remitente</label>
                        <input value={smtpConfig.from_name||""} placeholder="Fernández Lacort"
                          onChange={e => setSmtpConfig(p=>({...p,from_name:e.target.value}))} style={sInput} />
                      </div>
                      <div>
                        <label style={sLabel}>Reply-To</label>
                        <input value={smtpConfig.reply_to||""} placeholder="hola@fernandezlacort.com.ar"
                          onChange={e => setSmtpConfig(p=>({...p,reply_to:e.target.value}))} style={sInput} />
                      </div>
                      {/* Host manual — visible solo en "Otro" */}
                      {!PROVEEDORES_SMTP.slice(0,-1).find(p=>p.host===smtpConfig.host) && (
                        <>
                          <div>
                            <label style={sLabel}>Servidor SMTP</label>
                            <input value={smtpConfig.host||""} placeholder="mail.tudominio.com"
                              onChange={e => setSmtpConfig(p=>({...p,host:e.target.value}))} style={sInput} />
                          </div>
                          <div>
                            <label style={sLabel}>Puerto</label>
                            <input value={smtpConfig.port||""} placeholder="587"
                              onChange={e => setSmtpConfig(p=>({...p,port:e.target.value}))} style={sInput} />
                          </div>
                        </>
                      )}
                    </div>

                    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:24 }}>
                      <button onClick={probarSmtp} style={sBtn(false)}>{smtpStatus==="testing" ? "⏳ Probando..." : "📤 Probar (lead 9999)"}</button>
                      <button onClick={guardarSmtp} style={sBtn(true)}>{smtpStatus==="saving" ? "⏳ Guardando..." : "💾 Guardar"}</button>
                      {smtpStatus==="ok" && <span style={{ fontSize:12, color:"#059669" }}>✓ OK</span>}
                      {smtpStatus==="error" && <span style={{ fontSize:12, color:"#DC2626" }}>✗ Revisá credenciales</span>}
                    </div>
                  </div>

                  {/* SECCIÓN 4: USUARIOS */}
                  <div style={{ borderTop:"1px solid rgba(26,43,74,0.08)", paddingTop:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#1A2B4A", marginBottom:4 }}>4. Usuarios</div>
                    <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)", marginBottom:4 }}>Cada usuario extra entra con su nombre y un PIN de 4 dígitos.</div>
                    <div style={{ fontSize:11, color:"rgba(26,43,74,0.35)", marginBottom:12, fontStyle:"italic" }}>Plan base: 1 admin + hasta {MAX_VENDEDORES_PLAN - 1} usuarios extra.</div>

                    <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:12 }}>
                      {vendedores.map((v,i) => (
                        <div key={v.id} style={{
                          display:"flex", alignItems:"center", justifyContent:"space-between",
                          padding:"8px 10px", border:"1px solid rgba(26,43,74,0.1)", borderRadius:6, background:"#f8f7f4",
                        }}>
                          <span style={{ fontSize:13, color:"#1A2B4A" }}>
                            {v.nombre} {v.rol==="admin" && <span style={{ fontSize:10, color:"rgba(26,43,74,0.4)" }}>(admin)</span>}
                          </span>
                          <span style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ fontFamily:"monospace", fontSize:12, color:"rgba(26,43,74,0.5)" }}>PIN {v.pin}</span>
                            {i > 0 && (
                              <button onClick={() => eliminarVendedor(v.id)}
                                style={{ background:"none", border:"none", color:"#DC2626", cursor:"pointer", fontSize:12 }}>
                                Eliminar
                              </button>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>

                    {vendedores.length < MAX_VENDEDORES_PLAN ? (
                      <div style={{ display:"flex", gap:8 }}>
                        <input value={nuevoVendedorNombre} onChange={e => setNuevoVendedorNombre(e.target.value)}
                          placeholder="Nombre del nuevo usuario" style={{ ...sInput, flex:1 }} />
                        <button onClick={agregarVendedor} style={sBtn(true)}>+ Agregar</button>
                      </div>
                    ) : (
                      <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)", fontStyle:"italic" }}>
                        Llegaste al límite de usuarios de tu plan. Para sumar más, hay que pasar a un plan superior.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── FILA 2: CONOCIMIENTO (ancho completo) ── */}
              <div style={sCard}>
                <div style={{ fontWeight:700, fontSize:14, color:"#1A2B4A", marginBottom:4 }}>5. Conocimiento del agente</div>
                <div style={{ fontSize:12, color:"rgba(26,43,74,0.4)", marginBottom:16 }}>El "alma" del agente: quién sos, qué ofrecés y cómo calificar un buen lead.</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:14 }}>
                  {[
                    { icon:"🏢", label:"Perfil de empresa",    desc:"Quiénes son, qué ofrecen" },
                    { icon:"💰", label:"Lista de precios",      desc:"Precios por tipo de servicio" },
                    { icon:"❓", label:"Objeciones frecuentes", desc:"Cómo responder las comunes" },
                    { icon:"📋", label:"Protocolo de ventas",   desc:"Cómo trabajar cada oportunidad" },
                  ].map(item => (
                    <div key={item.label} style={{ display:"flex", flexDirection:"column", gap:8, padding:"14px", background:"rgba(26,43,74,0.02)", borderRadius:10, border:"1px solid rgba(26,43,74,0.07)" }}>
                      <span style={{ fontSize:22 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:"#1A2B4A", marginBottom:2 }}>{item.label}</div>
                        <div style={{ fontSize:11, color:"rgba(26,43,74,0.4)" }}>{item.desc}</div>
                      </div>
                      <label style={{ marginTop:"auto", padding:"6px 12px", borderRadius:7, border:"1px solid rgba(26,43,74,0.2)", background:"rgba(26,43,74,0.04)", color:"#1A2B4A", fontSize:11, cursor:"pointer", fontWeight:600, textAlign:"center" }}>
                        Subir archivo
                        <input type="file" accept=".pdf,.docx,.xlsx,.txt,.csv" style={{ display:"none" }}
                          onChange={e => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = ev => {
                              fetch(`https://fl.velqo.io/api/knowledge/${configEmpresa}`, {
                                method:"POST",
                                headers:{ "Content-Type":"application/octet-stream", "X-Filename":file.name },
                                body: ev.target.result,
                              }).then(r=>r.json()).then(d => {
                                if (d.ok) alert(`✅ "${file.name}" procesado`);
                                else alert(`❌ Error: ${d.error}`);
                              }).catch(() => alert("❌ Error al subir"));
                            };
                            reader.readAsArrayBuffer(file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
                <KnowledgeViewer empresa={configEmpresa} />
              </div>
            </div>
          );
        })()}

        {/* ── STATUS SISTEMA ── */}
        {tab === "dashboard" && sysStatus && (
          <div style={{ marginTop:14, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{
                  width:8, height:8, borderRadius:"50%",
                  background: Object.values(sysStatus.procesos).every(p=>p.ok) ? "#10B981" : "#F59E0B",
                  boxShadow: "0 0 6px " + (Object.values(sysStatus.procesos).every(p=>p.ok) ? "#10B981" : "#F59E0B"),
                }}/>
                <span style={{ fontWeight:700, fontSize:13, color:"#1A2B4A" }}>
                  {Object.values(sysStatus.procesos).every(p=>p.ok) ? "Sistema operativo" : "Sistema con advertencias"}
                </span>
              </div>
              {sysStatus.ultima_actualizacion && (
                <span style={{ fontSize:10, color:"rgba(26,43,74,0.3)" }}>
                  Ultima actualizacion: {sysStatus.ultima_actualizacion}
                </span>
              )}
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {Object.entries(sysStatus.procesos)
                .filter(([nombre]) => nombre === "captio-api" || nombre.includes("aarti"))
                .map(([nombre, estado]) => (
                <div key={nombre} style={{
                  display:"flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:20,
                  background: estado.ok ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)",
                  border: "1px solid " + (estado.ok ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"),
                }}>
                  <span style={{ fontSize:10 }}>{estado.ok ? "✅" : "⚠️"}</span>
                  <span style={{ fontSize:11, color: estado.ok ? "#10B981" : "#F59E0B", fontWeight:600 }}>{nombre}</span>
                  {!estado.ok && estado.error && (
                    <span style={{ fontSize:9, color:"rgba(26,43,74,0.35)", maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {estado.error.replace(/[^a-zA-Z0-9 ]/g,"").trim().slice(0,40)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CONOCIMIENTO ── */}
        {/* ── ADMIN ── */}
        {tab === "admin" && esAdmin && (
          <div>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontWeight:800, fontSize:18, color:"#1A2B4A", marginBottom:4 }}>Panel de Control</div>
              <div style={{ fontSize:12, color:"rgba(26,43,74,0.35)" }}>Análisis completo del pipeline · Fernández Lacort</div>
            </div>

            {/* FILA 1 — 6 KPI Cards */}
            {(() => {
              const hoy = new Date();
              const hace30 = new Date(hoy); hace30.setDate(hoy.getDate()-30);
              const emailsEnviados = leads.filter(l=>l.estado_email).length;
              const contactados = leads.filter(l=>l.estado_email).length;
              const kpis = [
                { label:"Total Leads",      value: leads.length,                                          color:"#1A2B4A", icon:"📋" },
                { label:"Emails Enviados",  value: emailsEnviados,                                        color:"#C8972A", icon:"✉️" },
                { label:"Contactados",      value: contactados,                                            color:"#3B82F6", icon:"📞" },
                { label:"Validados",        value: leads.filter(l=>l.estado==="Validado").length,          color:"#8B5CF6", icon:"✅" },
                { label:"Nuevos",           value: leads.filter(l=>l.estado==="Nuevo").length,             color:"#6B7280", icon:"🆕" },
                { label:"Ganados",          value: leads.filter(l=>l.estado==="Ganado").length,            color:"#10B981", icon:"🏆" },
              ];
              return (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:12, marginBottom:20 }}>
                  {kpis.map((k,i) => (
                    <div key={i} style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:12, padding:"16px 14px", borderTop:`3px solid ${k.color}` }}>
                      <div style={{ fontSize:18, marginBottom:6 }}>{k.icon}</div>
                      <div style={{ fontSize:22, fontWeight:800, color:k.color, marginBottom:2 }}>{k.value}</div>
                      <div style={{ fontSize:10, color:"rgba(26,43,74,0.4)", textTransform:"uppercase", letterSpacing:1 }}>{k.label}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* FILA 2 — 3 columnas: Leads por país · Pipeline tabla · Leads por región */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16, marginBottom:16 }}>

              {/* Leads por país */}
              {(() => {
                const paises = ["LATAM","China","France","Italy","Germany"];
                const cols = ["#8B5CF6","#1A2B4A","#06B6D4","#10B981","#F59E0B"];
                const data = paises.map((p,i) => ({
                  label: p, color: cols[i],
                  total: leads.filter(l=>l.region===p||l.ciudad?.includes(p)).length,
                })).concat([{ label:"Otros", color:"#9CA3AF", total: leads.filter(l=>!paises.some(p=>l.region===p||l.ciudad?.includes(p))).length }])
                  .filter(d=>d.total>0);
                const max = Math.max(...data.map(d=>d.total),1);
                return (
                  <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A", marginBottom:16 }}>🌍 Leads por país / mercado</div>
                    <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse:"collapse" }}>
                      <thead>
                        <tr>
                          <td style={{ fontSize:10, color:"rgba(26,43,74,0.35)", paddingBottom:8, textTransform:"uppercase", letterSpacing:1 }}>Mercado</td>
                          <td style={{ fontSize:10, color:"rgba(26,43,74,0.35)", paddingBottom:8, textAlign:"right", textTransform:"uppercase", letterSpacing:1 }}>Leads</td>
                          <td style={{ fontSize:10, color:"rgba(26,43,74,0.35)", paddingBottom:8, textAlign:"right", textTransform:"uppercase", letterSpacing:1 }}>%</td>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map(d => (
                          <tr key={d.label} style={{ borderTop:"1px solid rgba(26,43,74,0.05)" }}>
                            <td style={{ padding:"8px 0", fontSize:12, color:"#1A2B4A" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ width:8, height:8, borderRadius:"50%", background:d.color, flexShrink:0 }}/>
                                {d.label}
                              </div>
                            </td>
                            <td style={{ padding:"8px 0", fontSize:13, fontWeight:700, color:d.color, textAlign:"right" }}>{d.total}</td>
                            <td style={{ padding:"8px 0", fontSize:11, color:"rgba(26,43,74,0.35)", textAlign:"right" }}>{Math.round(d.total/leads.length*100)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Estado del pipeline — tabla */}
              {(() => {
                const estados = [
                  { id:"Nuevo",    color:"#6B7280", icon:"🔵" },
                  { id:"Validado", color:"#3B82F6", icon:"✅" },
                  { id:"Cotizado", color:"#F59E0B", icon:"📄" },
                  { id:"Ganado",   color:"#10B981", icon:"🏆" },
                  { id:"Perdido",  color:"#EF4444", icon:"❌" },
                ];
                const data = estados.map(e => ({
                  ...e,
                  total: leads.filter(l=>l.estado===e.id).length,
                  monto: leads.filter(l=>l.estado===e.id&&l.monto>0).reduce((a,b)=>a+b.monto,0),
                }));
                const total = data.reduce((a,b)=>a+b.total,0)||1;
                return (
                  <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A", marginBottom:16 }}>📊 Estado del pipeline</div>
                    <table width="100%" cellPadding="0" cellSpacing="0" style={{ borderCollapse:"collapse" }}>
                      <thead>
                        <tr>
                          <td style={{ fontSize:10, color:"rgba(26,43,74,0.35)", paddingBottom:8, textTransform:"uppercase", letterSpacing:1 }}>Estado</td>
                          <td style={{ fontSize:10, color:"rgba(26,43,74,0.35)", paddingBottom:8, textAlign:"right", textTransform:"uppercase", letterSpacing:1 }}>N°</td>
                          <td style={{ fontSize:10, color:"rgba(26,43,74,0.35)", paddingBottom:8, textAlign:"right", textTransform:"uppercase", letterSpacing:1 }}>Monto</td>
                        </tr>
                      </thead>
                      <tbody>
                        {data.map(d => (
                          <tr key={d.id} style={{ borderTop:"1px solid rgba(26,43,74,0.05)" }}>
                            <td style={{ padding:"9px 0", fontSize:12, color:"#1A2B4A" }}>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ width:8, height:8, borderRadius:"50%", background:d.color, flexShrink:0 }}/>
                                {d.id}
                              </div>
                            </td>
                            <td style={{ padding:"9px 0", textAlign:"right" }}>
                              <span style={{ fontSize:13, fontWeight:700, color:d.color }}>{d.total}</span>
                              <span style={{ fontSize:10, color:"rgba(26,43,74,0.3)", marginLeft:4 }}>{Math.round(d.total/total*100)}%</span>
                            </td>
                            <td style={{ padding:"9px 0", fontSize:11, color:"#C8972A", textAlign:"right", fontWeight:600 }}>
                              {d.monto>0 ? `$${d.monto.toLocaleString()}` : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}

              {/* Leads por región */}
              {(() => {
                const regiones = Array.from(new Set(leads.map(l=>l.region).filter(Boolean)));
                const cols = ["#1A2B4A","#C8972A","#3B82F6","#10B981","#8B5CF6","#F59E0B","#06B6D4","#EF4444"];
                const data = regiones.map((r,i) => ({
                  label: r, color: cols[i%8],
                  total: leads.filter(l=>l.region===r).length,
                  ganados: leads.filter(l=>l.region===r&&l.estado==="Ganado").length,
                })).sort((a,b)=>b.total-a.total);
                const max = Math.max(...data.map(d=>d.total),1);
                return (
                  <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A", marginBottom:16 }}>📍 Leads por región</div>
                    {data.length === 0 && <div style={{ fontSize:12, color:"rgba(26,43,74,0.3)", textAlign:"center", padding:"20px 0" }}>Sin datos de región</div>}
                    {data.map(d => (
                      <div key={d.label} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:12, color:"rgba(26,43,74,0.7)" }}>{d.label}</span>
                          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                            {d.ganados>0 && <span style={{ fontSize:10, color:"#10B981" }}>✓{d.ganados}</span>}
                            <span style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.total}</span>
                          </div>
                        </div>
                        <div style={{ height:5, background:"rgba(26,43,74,0.06)", borderRadius:3 }}>
                          <div style={{ height:"100%", width:`${Math.round(d.total/max*100)}%`, background:d.color, borderRadius:3 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

            </div>

            {/* FILA 3 — Gráfico captación últimos 30 días */}
            {(() => {
              const hoy = new Date();
              const dias = Array.from({length:30},(_,i)=>{
                const d = new Date(hoy);
                d.setDate(hoy.getDate()-29+i);
                return d.toISOString().split("T")[0];
              });
              const counts = dias.map(dia => ({
                dia,
                label: dia.slice(5),
                total: leads.filter(l=>l.fecha&&l.fecha.startsWith(dia)).length,
              }));
              const maxVal = Math.max(...counts.map(c=>c.total),1);
              const W = 600, H = 120, PAD = 20;
              const bw = (W-PAD*2)/30;
              return (
                <div style={{ background:"#ffffff", border:"1px solid rgba(26,43,74,0.1)", borderRadius:14, padding:20 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:13, color:"#1A2B4A" }}>📈 Captación de leads — últimos 30 días</div>
                      <div style={{ fontSize:11, color:"rgba(26,43,74,0.35)", marginTop:2 }}>
                        Total: {counts.reduce((a,c)=>a+c.total,0)} leads captados
                      </div>
                    </div>
                  </div>
                  <div style={{ overflowX:"auto" }}>
                    <svg width="100%" viewBox={`0 0 ${W} ${H+40}`} style={{ display:"block" }}>
                      {/* Grid lines */}
                      {[0,0.25,0.5,0.75,1].map((p,i)=>(
                        <line key={i} x1={PAD} y1={PAD+H*(1-p)} x2={W-PAD} y2={PAD+H*(1-p)}
                          stroke="rgba(26,43,74,0.06)" strokeWidth="1"/>
                      ))}
                      {/* Barras */}
                      {counts.map((c,i)=>{
                        const bh = maxVal>0 ? Math.max((c.total/maxVal)*H,c.total>0?3:0) : 0;
                        const x = PAD + i*bw + bw*0.15;
                        const bwActual = bw*0.7;
                        return (
                          <g key={i}>
                            <rect x={x} y={PAD+H-bh} width={bwActual} height={bh}
                              fill={c.total>0?"#1A2B4A":"rgba(26,43,74,0.06)"} rx="2"/>
                            {c.total>0 && (
                              <text x={x+bwActual/2} y={PAD+H-bh-4} textAnchor="middle"
                                fill="#C8972A" fontSize="9" fontWeight="700">{c.total}</text>
                            )}
                          </g>
                        );
                      })}
                      {/* Etiquetas eje X — cada 5 días */}
                      {counts.filter((_,i)=>i%5===0||i===29).map((c,i,arr)=>(
                        <text key={c.dia} x={PAD+(counts.indexOf(c))*bw+bw/2} y={PAD+H+16}
                          textAnchor="middle" fill="rgba(26,43,74,0.35)" fontSize="9">{c.label}</text>
                      ))}
                    </svg>
                  </div>
                </div>
              );
            })()}

          </div>
        )}
      </div>
      {/* MODALES */}
      {modal?.tipo === "mover" && (
        <ModalMover
          lead={modal.lead}
          destino={modal.destino}
          onConfirm={confirmarMover}
          onCancel={() => setModal(null)}
        />
      )}
      {modal?.tipo === "detalle" && (
        <ModalDetalle
          lead={modal.lead}
          vendedores={vendedores}
          onClose={() => setModal(null)}
          onAsignar={asignarVendedor}
          esAdmin={esAdmin}
          usuarioActual={usuario}
        />
      )}
    </div>
  );
}
